import { vttToLines, parseTimestamp, templateToScenes, supportedFonts, crossFade, getSizeDetails, appendFontFamilies } from "@vouchfor/canvas-video";
import { noChange, unsafeCSS, css, LitElement, html } from "lit";
import { property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { createRef, ref } from "lit/directives/ref.js";
import { styleMap } from "lit/directives/style-map.js";
import { Task } from "@lit/task";
import pMemoize from "p-memoize";
import Hls from "hls.js";
import { AsyncDirective } from "lit/async-directive.js";
import { directive, PartType, Directive } from "lit/directive.js";
import flatten from "flat";
import memoize from "memoize-one";
import { repeat } from "lit/directives/repeat.js";
import tippy from "tippy.js";
class MediaPlayerError extends Error {
  constructor(message, inheritedError = new Error(), fatal = false) {
    super();
    this.fatal = false;
    this.fatal = fatal;
    this.stack = inheritedError.stack;
    this.message = [message, inheritedError.message].filter(Boolean).join(" - ");
  }
}
const UPCOMING_OFFSET = 5;
class DrawController {
  constructor(host) {
    this._caption = "";
    this._currentTime = 0;
    this._activeScenes = [];
    this._upcomingScenes = [];
    this._canvas = createRef();
    this._controls = createRef();
    this.host = host;
  }
  set activeScenes(value) {
    if (value !== this._activeScenes) {
      const newFirstActiveScene = value[0];
      const prevFirstActiveScene = this._activeScenes[0];
      this._activeScenes = value;
      if (newFirstActiveScene !== prevFirstActiveScene) {
        this.host.dispatchEvent(new CustomEvent("scenechange", { detail: value[0] }));
        this.host.requestUpdate();
      }
    }
  }
  get activeScenes() {
    return this._activeScenes;
  }
  set currentTime(value) {
    var _a, _b, _c, _d, _e;
    const clampedTime = Math.min(this.host.duration, Math.max(0, value));
    this._currentTime = clampedTime;
    this.activeScenes = ((_a = this.host.scenes) == null ? void 0 : _a.filter(({ start, duration }) => {
      return clampedTime >= start && clampedTime < start + duration;
    })) ?? [];
    this._upcomingScenes = ((_b = this.host.scenes) == null ? void 0 : _b.filter(({ start }) => clampedTime >= start - UPCOMING_OFFSET && clampedTime < start)) ?? [];
    this.loadScenes();
    this.syncVideoState();
    (_c = this._controls.value) == null ? void 0 : _c.setAttribute("currentTime", clampedTime.toString());
    const lastActiveScene = this.activeScenes[0];
    const lines = vttToLines((_d = lastActiveScene == null ? void 0 : lastActiveScene.videoMeta) == null ? void 0 : _d.captions);
    this.caption = ((_e = lines.find(({ start, end }) => {
      var _a2;
      const normalisedTime = clampedTime - lastActiveScene.start + (((_a2 = lastActiveScene.videoMeta) == null ? void 0 : _a2.start) ?? 0);
      return start && end && normalisedTime >= parseTimestamp(start) && normalisedTime <= parseTimestamp(end);
    })) == null ? void 0 : _e.text) ?? "";
    this.draw(clampedTime);
    if (!this.host.paused && !this.host.loading && !this.host.processing && !this.host.waiting) {
      this.host.dispatchEvent(new CustomEvent("timeupdate", { detail: clampedTime }));
    }
  }
  get currentTime() {
    return this._currentTime;
  }
  set caption(value) {
    if (value !== this._caption) {
      this._caption = value;
      this.host.requestUpdate();
    }
  }
  get caption() {
    return this._caption;
  }
  canvas() {
    return ref(this._canvas);
  }
  controls() {
    return ref(this._controls);
  }
  loadScenes() {
    [...this.activeScenes, ...this._upcomingScenes].forEach((scene) => {
      var _a, _b, _c;
      const { start, duration, video, videoMeta } = scene;
      const shouldLoad = this.host.currentTime > 0 || this.host.preload === "auto" || !this.host.paused;
      if (video && videoMeta) {
        const { key, hls, node } = video;
        const { start: videoStart, duration: videoDuration } = videoMeta;
        const videoState = this.host.videoState[key];
        if (node && videoState) {
          const ended = node.currentTime >= videoStart + videoDuration || node.ended;
          if (!ended) {
            if (shouldLoad) {
              if (hls) {
                (_a = hls.startLoadOnce) == null ? void 0 : _a.call(hls, node.currentTime);
              } else if (!videoState.initialised) {
                (_b = node.startLoadOnce) == null ? void 0 : _b.call(node);
              }
            }
          }
        }
      } else if (shouldLoad) {
        const key = `${start}-${duration}`;
        const state2 = this.host._sceneController.videoState[key];
        if (this.host.duration && !((_c = state2 == null ? void 0 : state2.buffered) == null ? void 0 : _c.length)) {
          this.host._sceneController.videoState = {
            ...this.host._sceneController.videoState,
            [key]: {
              ...state2,
              buffered: [
                {
                  start: Math.max(0, start / this.host.duration * 100),
                  end: Math.min((start + duration) / this.host.duration * 100, 100)
                }
              ]
            }
          };
        }
      }
    });
  }
  syncVideoState() {
    var _a;
    (_a = this.host.scenes) == null ? void 0 : _a.forEach((scene) => {
      if (scene.video) {
        const { key, node } = scene.video;
        const videoState = this.host.videoState[key];
        const isActive = this.activeScenes.includes(scene);
        if (node && videoState) {
          if (isActive && !this.host.waiting && !this.host.loading && !this.host.paused) {
            if (node.paused && videoState.initialised) {
              try {
                return node.play();
              } catch (e) {
                throw new MediaPlayerError("Video play error", e, true);
              }
            }
          } else if (!node.paused) {
            node.pause();
          }
        }
      }
    });
  }
  draw(time = this.currentTime) {
    var _a, _b;
    if (this.host.sceneConfig && this._canvas.value) {
      (_b = (_a = this.host).compilation) == null ? void 0 : _b.call(_a, {
        currentTime: time,
        scenes: this.host.sceneConfig,
        canvas: this._canvas.value
      });
    }
  }
  clear() {
    var _a;
    const canvas = this._canvas.value;
    (_a = canvas == null ? void 0 : canvas.getContext("2d")) == null ? void 0 : _a.clearRect(0, 0, canvas.width, canvas.height);
  }
}
function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}
function isiOS() {
  return ["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(navigator.platform) || // iPad on iOS 13 detection
  navigator.userAgent.includes("Mac") && "ontouchend" in document;
}
function createBaseVideo(config) {
  const { id, key, src, playlist, duration } = config;
  return new Promise((resolve) => {
    let hls = null;
    const videoEl = document.createElement("video");
    videoEl.playsInline = true;
    videoEl.muted = true;
    videoEl.loop = false;
    if (playlist && !isSafari() && Hls.isSupported()) {
      let loadStarted = false;
      hls = new Hls({
        autoStartLoad: true,
        xhrSetup: (xhr) => {
          const { open } = xhr;
          const token = playlist.split("?")[1];
          xhr.open = function(method, url, async) {
            if (!url.toString().includes("?")) {
              url += "?" + token;
            }
            open.apply(this, [method, url, async]);
          };
        }
      });
      hls.loadSource(playlist);
      hls.attachMedia(videoEl);
      hls.startLoadOnce = (startPosition = -1, videoResolution) => {
        if (hls && !loadStarted) {
          const levelIndex = hls == null ? void 0 : hls.levels.findIndex((level) => Math.min(level.height, level.width) === videoResolution);
          hls.startLevel = hls.firstLevel;
          hls.startLoad(startPosition);
          loadStarted = true;
          hls.currentLevel = levelIndex !== -1 ? levelIndex : hls.firstLevel;
        }
      };
      hls.on(Hls.Events.LEVEL_LOADED, async () => {
        if (!loadStarted) {
          hls == null ? void 0 : hls.stopLoad();
        }
        resolve({
          id,
          key,
          hls,
          config,
          node: videoEl
        });
      });
    } else {
      videoEl.src = src;
      let loadStarted = false;
      videoEl.startLoadOnce = () => {
        if (!loadStarted) {
          videoEl.preload = "auto";
          videoEl.load();
          loadStarted = true;
        }
      };
      if (duration) {
        videoEl.preload = "none";
        resolve({
          id,
          key,
          hls,
          config,
          node: videoEl
        });
      } else {
        videoEl.preload = "metadata";
        const onLoadedMetaData = async () => {
          resolve({
            id,
            key,
            hls,
            config,
            node: videoEl
          });
          videoEl.removeEventListener("loadedmetadata", onLoadedMetaData);
        };
        videoEl.addEventListener("loadedmetadata", onLoadedMetaData);
      }
    }
  });
}
async function getDuration(node) {
  if (node.duration === Infinity) {
    return new Promise((resolve) => {
      node.currentTime = Number.MAX_SAFE_INTEGER;
      node.ontimeupdate = () => {
        node.ontimeupdate = null;
        resolve(node.duration);
        node.currentTime = 0;
      };
    });
  }
  return node.duration;
}
const loadFont = pMemoize(
  async function loadFont2(font) {
    const fontFace = new FontFace(font.name, `url(${font.woff2 ?? font.url})`, {
      style: font.style,
      weight: font.weight
    });
    return fontFace.load();
  },
  {
    cacheKey: ([font]) => JSON.stringify(font)
  }
);
let SceneController$1 = class SceneController {
  constructor(host) {
    this._hasPlayed = false;
    this._memoKey = null;
    this._lastDeps = null;
    this._duration = 0;
    this._scenes = null;
    this._videos = {};
    this._videoMeta = {};
    this._videoState = {};
    this._resolveVideo = pMemoize(
      async (config) => {
        const { id, key } = config;
        const video = await createBaseVideo(config);
        video.node.volume = this.host.volume;
        video.node.playbackRate = this.host.playbackRate;
        const listeners = {
          loadeddata: this._handleLoadedData,
          waiting: this._handleWaiting,
          canplay: this._handleCanPlay,
          progress: this._handleProgress,
          seeking: this._handleSeeking,
          seeked: this._handleSeeked,
          play: this._handlePlay,
          playing: this._handlePlaying,
          pause: this._handlePause,
          stalled: this._handleStalled,
          timeupdate: this._handleTimeUpdate,
          ended: this._handleEnded,
          error: this._handleError
        };
        const l = Object.keys(listeners).reduce((acc, event) => {
          const e = event;
          const listener = listeners[e]({ id, key, node: video.node });
          acc[e] = listener;
          video.node.addEventListener(event, listener);
          return acc;
        }, {});
        return {
          ...video,
          listeners: l
        };
      },
      {
        cacheKey: ([{ id, key, src, playlist }]) => {
          return JSON.stringify({ id, key, src: src == null ? void 0 : src.split("?")[0], playlist: playlist == null ? void 0 : playlist.split("?")[0] });
        }
      }
    );
    this._resolveVideoMeta = pMemoize(
      async (config, node) => {
        const { id, captions, duration, startOffset, endOffset } = config;
        const videoDuration = duration ?? await getDuration(node);
        const trimmedStart = (startOffset ?? 0) * videoDuration;
        const trimmedEnd = (1 - (endOffset ?? 0)) * videoDuration;
        return {
          id,
          captions,
          start: trimmedStart,
          duration: trimmedEnd - trimmedStart
        };
      },
      {
        cacheKey: ([{ id, key, captions, duration, startOffset, endOffset }]) => JSON.stringify({ id, key, captions, duration, startOffset, endOffset })
      }
    );
    this._handleLoadedData = ({ id, key, node }) => {
      return () => {
        var _a, _b;
        const state2 = this.videoState[key];
        if (!(state2 == null ? void 0 : state2.initialised)) {
          this.videoState = {
            ...this.videoState,
            [key]: {
              ...state2,
              initialised: true,
              waiting: false,
              // Sometimes the video seems to buffer before the onloadeddata callback is fired :/
              buffered: (state2 == null ? void 0 : state2.buffered) ?? []
            }
          };
          const scene = (_b = (_a = this.scenes) == null ? void 0 : _a.scenes) == null ? void 0 : _b.find(({ video }) => {
            key === (video == null ? void 0 : video.key);
          });
          if (scene) {
            this._setVideoCurrentTime({
              scene,
              time: this.host.currentTime
            });
          }
          this.host.dispatchEvent(new CustomEvent("video:loadeddata", { detail: { id, key, node } }));
        }
      };
    };
    this._handleWaiting = ({ key }) => {
      return () => {
        const state2 = this.videoState[key];
        if ((state2 == null ? void 0 : state2.initialised) && !(state2 == null ? void 0 : state2.waiting)) {
          this.videoState = {
            ...this.videoState,
            [key]: {
              ...state2,
              waiting: true
            }
          };
        }
      };
    };
    this._handleCanPlay = ({ key }) => {
      return () => {
        const state2 = this.videoState[key];
        if ((state2 == null ? void 0 : state2.initialised) && (state2 == null ? void 0 : state2.waiting)) {
          this.videoState = {
            ...this.videoState,
            [key]: {
              ...state2,
              waiting: false
            }
          };
        }
        if (this.host.paused) {
          this.host.draw();
        }
      };
    };
    this._handleProgress = ({ key, node }) => {
      return () => {
        const state2 = this.videoState[key];
        const bufferSegments = this._getBufferSegments({ key, node, duration: this.host.duration });
        this.videoState = {
          ...this.videoState,
          [key]: {
            ...state2,
            buffered: bufferSegments
          }
        };
      };
    };
    this._handleSeeking = ({ id, key, node }) => {
      return () => {
        this.host.dispatchEvent(new CustomEvent("video:seeking", { detail: { id, key, node } }));
      };
    };
    this._handleSeeked = ({ id, key, node }) => {
      return () => {
        const state2 = this.videoState[key];
        if (state2 == null ? void 0 : state2.initialised) {
          this.host.draw();
          this.host.dispatchEvent(new CustomEvent("video:seeked", { detail: { id, key, node } }));
        }
      };
    };
    this._handlePlay = ({ id, key, node }) => {
      return () => {
        this.hasPlayed = true;
        this.host.dispatchEvent(new CustomEvent("video:play", { detail: { id, key, node } }));
      };
    };
    this._handlePlaying = ({ id, key, node }) => {
      return () => {
        this.host.dispatchEvent(new CustomEvent("video:playing", { detail: { id, key, node } }));
      };
    };
    this._handlePause = ({ id, key, node }) => {
      return () => {
        this.host.dispatchEvent(new CustomEvent("video:pause", { detail: { id, key, node } }));
      };
    };
    this._handleStalled = ({ id, key, node }) => {
      return () => {
        this.host.dispatchEvent(new CustomEvent("video:stalled", { detail: { id, key, node } }));
      };
    };
    this._handleTimeUpdate = ({ id, key, node }) => {
      return () => {
        this.host.dispatchEvent(new CustomEvent("video:timeupdate", { detail: { id, key, node } }));
      };
    };
    this._handleEnded = ({ id, key, node }) => {
      return () => {
        this.host.dispatchEvent(new CustomEvent("video:ended", { detail: { id, key, node } }));
      };
    };
    this._handleError = ({ id, key, node }) => {
      return () => {
        const error = new MediaPlayerError(`Video error - answer ${id} failed to load`);
        this.host.dispatchEvent(
          new ErrorEvent("error", { bubbles: true, composed: true, error, message: error.message })
        );
        this.host.dispatchEvent(new CustomEvent("video:error", { detail: { id, key, node } }));
      };
    };
    this._cleanupVideo = (key) => {
      var _a;
      const video = this.videos[key];
      if (video) {
        video.node.pause();
        video.node.src = "";
        (_a = video.hls) == null ? void 0 : _a.destroy();
        Object.keys(video.listeners ?? {}).forEach((event) => {
          var _a2, _b;
          if ((_a2 = this.videos) == null ? void 0 : _a2[key]) {
            const listener = (_b = video.listeners) == null ? void 0 : _b[event];
            if (listener) {
              video.node.removeEventListener(event, listener);
            }
          }
        });
        delete this.videos[key];
      }
    };
    this.host = host;
    this.host.addController(this);
  }
  set hasPlayed(value) {
    if (value !== this._hasPlayed) {
      this._hasPlayed = value;
      this.host.requestUpdate();
    }
  }
  get hasPlayed() {
    return this._hasPlayed;
  }
  set duration(value) {
    if (!isNaN(value) && value !== this._duration) {
      this._duration = value;
      this.host.requestUpdate();
      this.host.dispatchEvent(new CustomEvent("durationchange", { detail: this._duration }));
    }
  }
  get duration() {
    return this._duration;
  }
  set scenes(value) {
    if (value !== this._scenes) {
      this._scenes = value;
      this.host.requestUpdate();
    }
  }
  get scenes() {
    return this._scenes;
  }
  set videos(value) {
    if (value !== this._videos) {
      this._videos = value;
      this.host.requestUpdate();
    }
  }
  get videos() {
    return this._videos;
  }
  set videoMeta(value) {
    if (value !== this._videoMeta) {
      this._videoMeta = value;
      this.host.requestUpdate();
    }
  }
  get videoMeta() {
    return this._videoMeta;
  }
  set videoState(value) {
    if (value !== this._videoState) {
      this._videoState = value;
      this.host.requestUpdate();
    }
  }
  get videoState() {
    return this._videoState;
  }
  _getBufferSegments({
    key,
    node,
    duration
  }) {
    var _a;
    const buffered = node == null ? void 0 : node.buffered;
    const bufferSegments = [];
    const { start, video, videoMeta } = ((_a = this.scenes) == null ? void 0 : _a.scenes.find(({ video: video2 }) => {
      return key === (video2 == null ? void 0 : video2.key);
    })) ?? {};
    if (node && buffered && video && videoMeta && typeof start !== "undefined") {
      for (let i = 0; i < buffered.length; i++) {
        const bufferStart = buffered.start(i);
        const bufferEnd = buffered.end(i);
        if (Math.max(bufferStart, videoMeta.start) <= Math.min(bufferEnd, videoMeta.start + videoMeta.duration)) {
          bufferSegments.push({
            // We need to offset the start and end of the buffer by the start time of this video's place in the compilation
            start: Math.max(0, (Math.max(bufferStart - videoMeta.start, 0) + start) / duration * 100),
            end: Math.min((Math.min(bufferEnd - videoMeta.start, videoMeta.duration) + start) / duration * 100, 100)
          });
        }
      }
    }
    return bufferSegments;
  }
  _setupTask() {
    new Task(
      this.host,
      async ([data, template, src, track, burnCaptions, debug]) => {
        try {
          let templateError = null;
          this.host._errors = [];
          if (src || data) {
            this.host._processing = true;
            if (src) {
              let captions = null;
              if (track) {
                try {
                  captions = await (await fetch(track, { method: "GET" })).text();
                } catch (e) {
                  const error = new MediaPlayerError("Track failed to fetch", e);
                  this.host.dispatchEvent(
                    new ErrorEvent("error", {
                      composed: true,
                      bubbles: true,
                      error,
                      message: error.message
                    })
                  );
                }
              }
              const mediaKey = src.includes(".m3u8") ? "playlist" : "video";
              const sourceData = {
                questions: {
                  items: [
                    {
                      answer: {
                        id: "source",
                        media: {
                          [mediaKey]: src
                        },
                        captions: {
                          current: captions
                        }
                      }
                    }
                  ]
                }
              };
              try {
                this.scenes = await templateToScenes(
                  {
                    data: sourceData,
                    template,
                    burnCaptions,
                    debug
                  },
                  this._resolveVideo,
                  this._resolveVideoMeta
                );
              } catch (e) {
                try {
                  this.scenes = await templateToScenes(
                    {
                      data: sourceData,
                      burnCaptions,
                      debug
                    },
                    this._resolveVideo,
                    this._resolveVideoMeta
                  );
                } catch (e2) {
                  throw new MediaPlayerError("Fatal source template error", e2, true);
                }
                templateError = new MediaPlayerError("Source template error", e);
              }
            } else {
              try {
                this.scenes = await templateToScenes(
                  { data, template, burnCaptions, debug },
                  this._resolveVideo,
                  this._resolveVideoMeta
                );
              } catch (e) {
                try {
                  this.scenes = await templateToScenes(
                    { data, burnCaptions, debug },
                    this._resolveVideo,
                    this._resolveVideoMeta
                  );
                } catch (e2) {
                  throw new MediaPlayerError("Fatal data template error", e2, true);
                }
                templateError = new MediaPlayerError("Data template error", e);
              }
            }
            Object.keys(this.videos).forEach((key) => {
              if (!this.scenes.scenes.find(({ video }) => key === (video == null ? void 0 : video.key))) {
                this._videos[key].node.pause();
              }
            });
            const currentSceneVideos = this.scenes.scenes.reduce((acc, { video }) => {
              if (video) {
                acc[video.key] = video;
              }
              return acc;
            }, {});
            if (Object.keys(this.videos).join("|") !== Object.keys(currentSceneVideos).join("|")) {
              this.host.reset(0, false);
            }
            this.videos = currentSceneVideos;
            this.videoState = {
              ...this.videoState,
              ...this.scenes.scenes.reduce((acc, { video, start, duration }) => {
                var _a;
                const key = (video == null ? void 0 : video.key) ?? `${start}-${duration}`;
                const bufferSegments = this._getBufferSegments({
                  key,
                  node: video == null ? void 0 : video.node,
                  duration: this.scenes.duration
                });
                acc[key] = {
                  // @ts-ignore
                  initialised: !video,
                  // @ts-ignore
                  waiting: !!video,
                  ...this.videoState[key],
                  buffered: video ? bufferSegments : (_a = this.videoState[key]) == null ? void 0 : _a.buffered
                };
                return acc;
              }, {})
            };
            this.videoMeta = this.scenes.scenes.reduce((acc, { video, videoMeta }) => {
              if (video && videoMeta) {
                acc[video.key] = videoMeta;
              }
              return acc;
            }, {});
            const fonts = await Promise.all(this.scenes.fonts.map((font) => loadFont(font)));
            fonts.forEach((font) => {
              document.fonts.add(font);
            });
            this.duration = this.scenes.duration;
            if (this.host.paused) {
              this.host.reset(this.host.currentTime, !this.host.paused);
            }
          }
          if (templateError) {
            this.host.dispatchEvent(
              new ErrorEvent("error", {
                composed: true,
                bubbles: true,
                error: templateError,
                message: templateError.message
              })
            );
          }
        } catch (e) {
          this.scenes = null;
          this.duration = 0;
          this.host.dispatchEvent(
            new ErrorEvent("error", { composed: true, bubbles: true, error: e, message: e.message })
          );
        } finally {
          this.host._processing = false;
        }
      },
      () => {
        const memoKey = JSON.stringify({
          data: this.host.data,
          template: this.host.template,
          src: this.host.src,
          track: this.host.track,
          burnCaptions: this.host.burnCaptions,
          debug: this.host.debug
        });
        if (this._lastDeps && this._memoKey === memoKey) {
          return this._lastDeps;
        }
        const deps = [
          this.host.data,
          this.host.template,
          this.host.src,
          this.host.track,
          this.host.burnCaptions,
          this.host.debug
        ];
        this._memoKey = memoKey;
        this._lastDeps = deps;
        return deps;
      }
    );
  }
  _setVideoCurrentTime({ time, scene }) {
    const { start, video, videoMeta } = scene;
    if (video) {
      if (video.node && videoMeta && typeof start !== "undefined") {
        const normalisedTime = time - start;
        video.node.currentTime = Math.max(
          // The lower bound is the trimmed video start
          videoMeta.start,
          Math.min(
            // The upper bound is the trimmed video end
            videoMeta.start + videoMeta.duration,
            // This is the time of the video as it's active
            normalisedTime + videoMeta.start
          )
        );
      }
    }
  }
  setVideosCurrentTime(time) {
    var _a;
    (_a = this.scenes) == null ? void 0 : _a.scenes.forEach((scene) => {
      this._setVideoCurrentTime({ time, scene });
    });
  }
  hostConnected() {
    this._setupTask();
  }
  hostDisconnected() {
    Object.keys(this.videos).forEach((key) => {
      this._cleanupVideo(key);
    });
    this.videos = {};
  }
};
function enterFullScreen(element) {
  if (element.requestFullscreen) {
    element.requestFullscreen();
  } else if (element.mozRequestFullScreen) {
    element.mozRequestFullScreen();
  } else if (element.webkitRequestFullscreen) {
    element.webkitRequestFullscreen();
  } else if (element.msRequestFullscreen) {
    element.msRequestFullscreen();
  }
}
function exitFullscreen() {
  if (document.exitFullscreen) {
    document.exitFullscreen();
  } else if (document.mozCancelFullScreen) {
    document.mozCancelFullScreen();
  } else if (document.webkitExitFullscreen) {
    document.webkitExitFullscreen();
  }
}
const defaultControls = [
  "progress",
  "play-large",
  "navigation",
  "play",
  "volume",
  "current-time",
  "duration",
  "speed",
  "captions",
  "fullscreen",
  "preview"
];
const captionsItem = {
  id: "captions",
  event: "captions:click",
  label: "Closed Captions"
};
const fullscreenItem = {
  id: "fullscreen",
  event: "fullscreen:click",
  label: "Full Screen"
};
const playbackRateItems = {
  title: "Playback Speed",
  items: [
    {
      id: "playback-rate-0.5",
      event: "playbackRate:click",
      label: "0.5x",
      value: 0.5
    },
    {
      id: "playback-rate-1",
      event: "playbackRate:click",
      label: "1.0x",
      value: 1
    },
    {
      id: "playback-rate-1.5",
      event: "playbackRate:click",
      label: "1.5x",
      value: 1.5
    },
    {
      id: "playback-rate-2",
      event: "playbackRate:click",
      label: "2.0x",
      value: 2
    }
  ]
};
const playbackItem = {
  id: "playbackRate",
  label: "Playback playbackRate",
  subItems: playbackRateItems
};
class ResizeObserverDirective extends AsyncDirective {
  constructor(partInfo) {
    super(partInfo);
    this.element = null;
    this.observer = void 0;
    if (partInfo.type !== PartType.ELEMENT) {
      throw new Error("Resize directive must be used in an Element expression");
    }
  }
  disconnected() {
    var _a;
    (_a = this.observer) == null ? void 0 : _a.disconnect();
    this.observer = void 0;
  }
  reconnected() {
    var _a;
    if (this.element) {
      (_a = this.observer) == null ? void 0 : _a.observe(this.element);
    }
  }
  update(part, [callback]) {
    if (this.isConnected && !this.observer) {
      this.observer = new ResizeObserver(callback);
      this.element = part.element;
      this.observer.observe(this.element);
    }
    return noChange;
  }
}
const resizeObserver = directive(ResizeObserverDirective);
const createFontFaces = memoize(() => {
  const roobertRegular = new FontFace(
    "Roobert",
    "url(https://media-player-fonts.s3.ap-southeast-2.amazonaws.com/Roobert-Regular.woff2)",
    {
      style: "normal",
      weight: "400"
    }
  );
  const roobertSemibold = new FontFace(
    "Roobert",
    "url(https://media-player-fonts.s3.ap-southeast-2.amazonaws.com/Roobert-SemiBold.woff2)",
    {
      style: "normal",
      weight: "600"
    }
  );
  const roobertCaptions = new FontFace(
    "Roobert Captions",
    "url(https://media-player-fonts.s3.ap-southeast-2.amazonaws.com/Roobert-Regular.woff2)",
    {
      style: "normal",
      weight: "400"
    }
  );
  const notoFonts = supportedFonts.map((font) => {
    return {
      load: false,
      font: new FontFace(font.family, `url(${font.url})`, {
        style: font.style
      })
    };
  });
  return [
    { load: true, font: roobertRegular },
    { load: true, font: roobertSemibold },
    { load: true, font: roobertCaptions },
    ...notoFonts
  ];
});
const colors = {
  "red-120": "#feeded",
  "red-130": "#fcd5d0",
  "red-400": "#e84a4a",
  "red-500": "#c51c3a",
  "red-700": "#a21c39",
  "red-800": "#850231",
  "red-900": "#580A2D",
  "teal-80": "#f3fafa",
  "teal-100": "#d6f1f1",
  "teal-200": "#ace3e3",
  "teal-300": "#7acace",
  "teal-400": "#4faab2",
  "teal-500": "#358f97",
  "teal-600": "#287179",
  "teal-700": "#22555c",
  "teal-800": "#21484e",
  "teal-900": "#1f3d42",
  "teal-950": "#0d2126",
  "purple-100": "#d7cfff",
  "purple-700": "#5f2fe9",
  "green-200": "#a4d7aa",
  "green-500": "#38b319",
  "green-800": "#2b3c2f",
  "grey-100": "#f0f0f0",
  "light-50": "rgba(255, 255, 255, .05)",
  "light-100": "rgba(255, 255, 255, .1)",
  "light-200": "rgba(255, 255, 255, .2)",
  "light-400": "rgba(255, 255, 255, .4)",
  "light-500": "rgba(255, 255, 255, .5)",
  "light-640": "rgba(255, 255, 255, .64)",
  "light-800": "rgba(255, 255, 255, .8)",
  "light-1000": "#fff",
  "dark-50": "rgba(0, 0, 0, .05)",
  "dark-80": "rgba(0, 0, 0, .08)",
  "dark-100": "rgba(0, 0, 0, .1)",
  "dark-200": "rgba(0, 0, 0, .2)",
  "dark-250": "rgba(0, 0, 0, .25)",
  "dark-400": "rgba(0, 0, 0, .4)",
  "dark-500": "rgba(0, 0, 0, .5)",
  "dark-640": "rgba(0, 0, 0, .64)",
  "dark-800": "rgba(0, 0, 0, .8)",
  "dark-900": "rgba(0, 0, 0, .9)",
  "dark-1000": "#000"
};
const tokens = {
  colors,
  space: {
    xxs: ".25rem",
    xs: ".5rem",
    small: ".75rem",
    medium: "1rem",
    large: "1.5rem",
    xl: "2.5rem"
  },
  sizes: {
    xxs: "1rem",
    xs: "1.5rem",
    small: "2rem",
    medium: "2.5rem",
    large: "3rem"
  },
  fontWeights: {
    regular: 400,
    semibold: 600
  },
  lineHeights: {
    small: 1.1,
    medium: 1.25,
    large: 1.4
  },
  letterSpacings: {
    xxs: "-.04rem",
    xs: "-.03rem",
    small: "-.02rem",
    medium: "-.01rem",
    large: 0
  },
  fontSizes: {
    micro: ".625rem",
    small: ".75rem",
    medium: ".875rem",
    large: "1rem",
    xl: "1.25rem",
    xxl: "1.5rem",
    xxxl: "2rem",
    huge: "2.75rem"
  },
  fontFamilies: {
    sans: `Roobert, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial,"Noto Sans", "Noto Color Emoji", sans-serif, "Segoe UI Emoji", "Segoe UI Symbol", "Apple Color Emoji"`,
    mono: `"Maison Neue Mono", Menlo, Monaco, Consolas, Liberation Mono, "Courier New", monospace`
  },
  borderWidths: {
    thin: ".0625rem"
  },
  radii: {
    small: ".25rem",
    medium: ".5rem",
    large: ".75rem",
    xl: "1rem"
  },
  shadows: {
    small: `.25rem .25rem .25rem ${colors["dark-100"]}`,
    medium: `0 .125rem .625rem ${colors["dark-100"]}`,
    large: `0 .25rem 1rem ${colors["dark-50"]}`,
    xl: `.25rem .25rem 1rem ${colors["dark-250"]}`,
    xxl: `0 .25rem 2rem ${colors["dark-100"]}`,
    xxxl: `.25rem .25rem 3rem ${colors["dark-250"]}`,
    toolbar: `0 -.625rem .625rem ${colors["dark-50"]}`,
    text: `0 0 .25rem ${colors["dark-250"]}`
  },
  timings: {
    fast: ".1s",
    normal: ".2s",
    slow: ".3s"
  },
  easings: {
    linear: "linear"
  },
  focusOutline: {
    width: ".2rem",
    color: colors["dark-400"]
  },
  iconSizes: {
    small: "0.75rem",
    medium: "1rem",
    large: "1.5rem"
  }
};
const flatTokens = flatten.flatten(tokens, { delimiter: "-" });
const theme = Object.keys(flatTokens).map((themeVar) => {
  return unsafeCSS(`:host { --${themeVar}: ${flatTokens[themeVar]}; }`);
});
const ThemeMixin = (SuperClass) => {
  class ThemeMixinClass extends SuperClass {
    // Add fonts to parent document as long as they don't exist already
    firstUpdated() {
      const fontFaces = createFontFaces();
      fontFaces.forEach(({ load, font }) => {
        document.fonts.add(font);
        if (load) {
          font.load();
        }
      });
    }
  }
  ThemeMixinClass.styles = [
    (SuperClass == null ? void 0 : SuperClass.styles) ?? [],
    theme,
    css`
        :host {
          font-family: var(--fontFamilies-sans);
          font-size: var(--fontSizes-medium);
          line-height: var(--lineHeights-medium);
          font-weight: var(--fontWeights-regular);
        }
      `,
    // Reset
    css`
        *,
        *::before,
        *::after,
        ::slotted(*) {
          box-sizing: border-box;
        }

        body,
        h1,
        h2,
        h3,
        h4,
        p,
        figure,
        blockquote,
        dl,
        dd,
        ::slotted(h1),
        ::slotted(h2),
        ::slotted(h3),
        ::slotted(h4),
        ::slotted(p),
        ::slotted(figure),
        ::slotted(blockquote),
        ::slotted(dl),
        ::slotted(dd) {
          margin: 0;
        }

        ul[role='list'],
        ol[role='list'],
        ::slotted(ul[role='list']),
        ::slotted(ol[role='list']) {
          list-style: none;
        }

        a:not([class]),
        ::slotted(a:not([class])) {
          text-decoration-skip-ink: auto;
        }

        img,
        picture,
        ::slotted(img),
        ::slotted(picture) {
          max-width: 100%;
          display: block;
        }

        input,
        button,
        textarea,
        select,
        ::slotted(input),
        ::slotted(button),
        ::slotted(textarea),
        ::slotted(select) {
          font: inherit;
        }

        @media (prefers-reduced-motion) {
          *,
          *::before,
          *::after,
          ::slotted(*),
          ::slotted(*::before),
          ::slotted(*::after) {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }
      `
  ];
  return ThemeMixinClass;
};
const ThemedLitElement = ThemeMixin(LitElement);
const legacyCustomElement = (tagName, clazz) => {
  if (typeof window !== "undefined") {
    window.customElements.get(tagName) ?? window.customElements.define(tagName, clazz);
  }
  return clazz;
};
const standardCustomElement = (tagName, descriptor) => {
  const { kind, elements } = descriptor;
  return {
    kind,
    elements,
    // This callback is called once the class is otherwise fully defined
    finisher(clazz) {
      if (typeof window !== "undefined") {
        window.customElements.get(tagName) ?? window.customElements.define(tagName, clazz);
      }
    }
  };
};
const customElement = (tagName) => (classOrDescriptor) => typeof classOrDescriptor === "function" ? legacyCustomElement(tagName, classOrDescriptor) : standardCustomElement(tagName, classOrDescriptor);
class SceneController2 {
  constructor(host) {
    this._memoKey = null;
    this._lastDeps = null;
    this._scenes = null;
    this._video = null;
    this._handleSeeked = () => {
      this.host._draw();
    };
    this._resolveVideo = pMemoize(
      async (config) => {
        const video = await createBaseVideo(config);
        video.node.play = async () => {
        };
        video.node.addEventListener("seeked", this._handleSeeked);
        return video;
      },
      {
        cacheKey: ([{ id, src, playlist }]) => {
          return JSON.stringify({ id, src: src == null ? void 0 : src.split("?")[0], playlist: playlist == null ? void 0 : playlist.split("?")[0] });
        }
      }
    );
    this._resolveAnswer = pMemoize(
      async (config) => {
        const { id, key, poster } = config;
        const { node } = await createBaseVideo(config);
        const image = new Image();
        return {
          id,
          key,
          config,
          node,
          getSource: () => {
            if (poster && image.src !== poster) {
              image.onload = () => {
                this.host._draw();
              };
              image.src = poster;
            }
            return image;
          }
        };
      },
      {
        cacheKey: ([{ id, src, playlist, poster }]) => {
          return JSON.stringify({
            id,
            src: src == null ? void 0 : src.split("?")[0],
            playlist: playlist == null ? void 0 : playlist.split("?")[0],
            poster: poster == null ? void 0 : poster.split("?")[0]
          });
        }
      }
    );
    this._resolveVideoMeta = pMemoize(
      async (config, node) => {
        const { id, duration, startOffset, endOffset } = config;
        const videoDuration = duration ?? await getDuration(node);
        const trimmedStart = (startOffset ?? 0) * videoDuration;
        const trimmedEnd = (1 - (endOffset ?? 0)) * videoDuration;
        return {
          id,
          start: trimmedStart,
          duration: trimmedEnd - trimmedStart
        };
      },
      {
        cacheKey: ([{ id, duration, startOffset, endOffset }]) => JSON.stringify({ id, duration, startOffset, endOffset })
      }
    );
    this.host = host;
    this.host.addController(this);
  }
  set scenes(value) {
    if (value !== this._scenes) {
      this._scenes = value;
      this.host.requestUpdate();
    }
  }
  get scenes() {
    return this._scenes;
  }
  _setupTask() {
    new Task(
      this.host,
      async ([src, data, template]) => {
        var _a, _b;
        try {
          if (src) {
            const mediaKey = src.includes(".m3u8") ? "playlist" : "video";
            try {
              this.scenes = await templateToScenes(
                {
                  data: {
                    questions: {
                      items: [
                        {
                          answer: {
                            id: "source",
                            media: {
                              [mediaKey]: src
                            }
                          }
                        }
                      ]
                    }
                  },
                  template
                },
                this._resolveVideo,
                this._resolveVideoMeta
              );
            } catch (e) {
              throw new MediaPlayerError("Video preview source template error", e);
            }
            this._video = ((_b = (_a = this.scenes.scenes[0]) == null ? void 0 : _a.video) == null ? void 0 : _b.node) ?? null;
          } else if (data) {
            try {
              this.scenes = await templateToScenes({ data, template }, this._resolveAnswer, this._resolveVideoMeta);
            } catch (e) {
              throw new MediaPlayerError("Video preview data template error", e);
            }
          }
        } catch (e) {
          this.scenes = null;
          this.host.dispatchEvent(
            new ErrorEvent("error", { composed: true, bubbles: true, error: e, message: e.message })
          );
        }
      },
      () => {
        const memoKey = JSON.stringify({
          data: this.host.data,
          template: this.host.template
        });
        if (this._lastDeps && this._memoKey === memoKey) {
          return this._lastDeps;
        }
        const deps = [this.host.src, this.host.data, this.host.template];
        this._memoKey = memoKey;
        this._lastDeps = deps;
        return deps;
      }
    );
  }
  hostConnected() {
    this._setupTask();
  }
  hostDisconnected() {
    if (this._video) {
      this._video.pause();
      this._video.removeEventListener("seeked", this._handleSeeked);
      this._video.src = "";
      this._video = null;
    }
  }
}
function clamp(min, value, max) {
  return Math.min(Math.max(value, min), max);
}
function formatDuration(seconds, options = {}) {
  const duration = Math.max(0, seconds || 0);
  const dHours = Math.floor(duration / 3600);
  const dMinutes = Math.floor(duration % 3600 / 60);
  const dSeconds = Math.floor(duration % 3600 % 60);
  const dMilliseconds = Math.floor(duration % 1 * 1e3);
  let durationStr = dHours ? `${dHours}:${`${dMinutes}`.padStart(2, "0")}:${`${dSeconds}`.padStart(2, "0")}` : `${dMinutes}:${`${dSeconds}`.padStart(2, "0")}`;
  if (options.includeMs) {
    durationStr += `:${`${dMilliseconds}`.padStart(3, "0")}`;
  }
  return durationStr;
}
var __defProp$d = Object.defineProperty;
var __getOwnPropDesc$d = Object.getOwnPropertyDescriptor;
var __decorateClass$d = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$d(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$d(target, key, result);
  return result;
};
let Button = class extends ThemedLitElement {
  constructor() {
    super(...arguments);
    this.active = false;
    this.size = "small";
    this.rounded = "medium";
    this.weight = "light";
  }
  render() {
    return html`<button
      class=${classMap({
      active: this.active,
      "size-tiny": this.size === "tiny",
      "size-small": this.size === "small",
      "size-large": this.size === "large",
      "radius-medium": this.rounded === "medium",
      "radius-full": this.rounded === "full",
      "weight-light": this.weight === "light",
      "weight-heavy": this.weight === "heavy"
    })}
    >
      <slot></slot>
      ${this.icon && html`<vmp-icon icon=${this.icon}></vmp-icon>`}
    </button>`;
  }
};
Button.styles = [
  ThemedLitElement.styles ?? [],
  css`
      :host {
        --button-padding: var(--vu-button-padding, var(--space-s));
        --button-color: var(--vu-button-color, var(--colors-dark-640));
        --button-background: var(--vu-button-background, var(--colors-light-1000));
        --button-background-hover: var(--vu-button-background-hover, var(--colors-light-1000));

        display: block;
      }

      button {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-xxs);
        border: none;
        cursor: pointer;
        font-weight: var(--fontWeights-semibold);
        pointer-events: all;
        transition: background 150ms ease;

        padding: var(--button-padding);
        color: var(--button-color);
        background: var(--button-background);
        padding: var(--button-padding);
      }

      button:focus {
        outline: var(--borderWidths-thin) solid var(--colors-dark-1000);
      }

      .size-tiny {
        --button-padding: var(--vu-button-padding, var(--space-xxs));
        min-width: 20px;
      }

      .size-small {
        --button-padding: var(--vu-button-padding, 6px);
        min-width: 32px;
      }

      .size-large {
        --button-padding: var(--vu-button-padding, var(--space-small));
        min-width: 48px;
      }

      .radius-medium {
        border-radius: var(--radii-small);
      }

      .radius-full {
        border-radius: 100rem;
      }

      .weight-light {
        --button-color: var(--vu-button-color, var(--colors-light-1000));
        --button-background: var(--vu-button-background, transparent);
      }

      .weight-heavy {
        --button-color: var(--vu-button-color, var(--colors-dark-640));
        --button-background: var(--vu-button-background, var(--colors-light-1000));
      }

      .weight-heavy.active,
      .weight-heavy:hover,
      .weight-heavy:focus {
        --button-background: var(--vu-button-background-hover, #cccccc);
      }

      .weight-light.active,
      .weight-light:hover,
      .weight-light:focus {
        --button-background: var(--vu-button-background-hover, #9d9d9d);
      }
    `
];
__decorateClass$d([
  property({ type: Boolean })
], Button.prototype, "active", 2);
__decorateClass$d([
  property({ type: String })
], Button.prototype, "size", 2);
__decorateClass$d([
  property({ type: String })
], Button.prototype, "rounded", 2);
__decorateClass$d([
  property({ type: String })
], Button.prototype, "weight", 2);
__decorateClass$d([
  property({ type: String })
], Button.prototype, "icon", 2);
Button = __decorateClass$d([
  customElement("vmp-button")
], Button);
const icons = {
  "arrow-left": () => html`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" part="svg">
      <path
        d="M16.6666 9.21875H6.52492L11.1833 4.85156L9.99992 3.75L3.33325 10L9.99992 16.25L11.1749 15.1484L6.52492 10.7812H16.6666V9.21875Z"
        fill="currentColor"
      />
    </svg>`,
  "arrow-right": () => html`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" part="svg">
      <path
        d="M3.75 9.21875H13.2578L8.89062 4.85156L10 3.75L16.25 10L10 16.25L8.89844 15.1484L13.2578 10.7812H3.75V9.21875Z"
        fill="currentColor"
      />
    </svg>`,
  "captions-cross": () => html`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" part="svg">
      <path
        d="M17.0625 19.4167L14.3125 16.6667H3.33333C2.875 16.6667 2.4825 16.5036 2.15583 16.1775C1.82972 15.8509 1.66667 15.4584 1.66667 15V4.16669C1.66667 4.13891 1.66667 4.11446 1.66667 4.09335C1.66667 4.0728 1.66667 4.04863 1.66667 4.02085L0.5625 2.91669L1.75 1.72919L18.25 18.2292L17.0625 19.4167ZM18.3333 15.9375L14.7083 12.3125C14.8056 12.2431 14.8786 12.1495 14.9275 12.0317C14.9758 11.9134 15 11.7917 15 11.6667V10.8334H13.75V11.25H13.6458L12.0833 9.68752V8.75002H13.75V9.16669H15V8.33335C15 8.09724 14.92 7.89919 14.76 7.73919C14.6006 7.57974 14.4028 7.50002 14.1667 7.50002H11.6667C11.4306 7.50002 11.2328 7.57974 11.0733 7.73919C10.9133 7.89919 10.8333 8.09724 10.8333 8.33335V8.43752L5.72917 3.33335H16.6667C17.125 3.33335 17.5175 3.49669 17.8442 3.82335C18.1703 4.14946 18.3333 4.54169 18.3333 5.00002V15.9375ZM5.83333 12.5H8.33333C8.56944 12.5 8.7675 12.42 8.9275 12.26C9.08694 12.1006 9.16667 11.9028 9.16667 11.6667V11.5209L8.47917 10.8334H7.91667V11.25H6.25V8.60419L5.3125 7.66669C5.21528 7.73613 5.13889 7.82974 5.08333 7.94752C5.02778 8.06585 5 8.19446 5 8.33335V11.6667C5 11.9028 5.07972 12.1006 5.23917 12.26C5.39917 12.42 5.59722 12.5 5.83333 12.5Z"
        fill="currentColor"
      />
    </svg>`,
  captions: () => html`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" part="svg">
      <path
        d="M5.83342 12.5H8.33342C8.56953 12.5 8.76758 12.42 8.92758 12.26C9.08703 12.1005 9.16675 11.9028 9.16675 11.6666V10.8333H7.91675V11.25H6.25008V8.74998H7.91675V9.16665H9.16675V8.33331C9.16675 8.0972 9.08703 7.89915 8.92758 7.73915C8.76758 7.5797 8.56953 7.49998 8.33342 7.49998H5.83342C5.5973 7.49998 5.39925 7.5797 5.23925 7.73915C5.0798 7.89915 5.00008 8.0972 5.00008 8.33331V11.6666C5.00008 11.9028 5.0798 12.1005 5.23925 12.26C5.39925 12.42 5.5973 12.5 5.83342 12.5ZM11.6667 12.5H14.1667C14.4029 12.5 14.6006 12.42 14.7601 12.26C14.9201 12.1005 15.0001 11.9028 15.0001 11.6666V10.8333H13.7501V11.25H12.0834V8.74998H13.7501V9.16665H15.0001V8.33331C15.0001 8.0972 14.9201 7.89915 14.7601 7.73915C14.6006 7.5797 14.4029 7.49998 14.1667 7.49998H11.6667C11.4306 7.49998 11.2329 7.5797 11.0734 7.73915C10.9134 7.89915 10.8334 8.0972 10.8334 8.33331V11.6666C10.8334 11.9028 10.9134 12.1005 11.0734 12.26C11.2329 12.42 11.4306 12.5 11.6667 12.5ZM3.33341 16.6666C2.87508 16.6666 2.48258 16.5036 2.15591 16.1775C1.8298 15.8508 1.66675 15.4583 1.66675 15V4.99998C1.66675 4.54165 1.8298 4.14942 2.15591 3.82331C2.48258 3.49665 2.87508 3.33331 3.33341 3.33331H16.6667C17.1251 3.33331 17.5176 3.49665 17.8442 3.82331C18.1704 4.14942 18.3334 4.54165 18.3334 4.99998V15C18.3334 15.4583 18.1704 15.8508 17.8442 16.1775C17.5176 16.5036 17.1251 16.6666 16.6667 16.6666H3.33341Z"
        fill="currentColor"
      />
    </svg>`,
  "chevron-down": () => html`<svg width="16" height="16" viewBox="0 0 16 16" fill="none" part="svg">
      <path
        d="M11.8032 7.13666L10.8632 6.19666L7.80322 9.24999L4.74322 6.19666L3.80322 7.13666L7.80322 11.1367L11.8032 7.13666Z"
        fill="currentColor"
      />
    </svg>`,
  close: () => html`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" part="svg">
      <path
        d="M15.8334 5.34163L14.6584 4.16663L10 8.82496L5.34169 4.16663L4.16669 5.34163L8.82502 9.99996L4.16669 14.6583L5.34169 15.8333L10 11.175L14.6584 15.8333L15.8334 14.6583L11.175 9.99996L15.8334 5.34163Z"
        fill="currentColor"
      />
    </svg>`,
  error: () => html`<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" part="svg">
      <path
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm1 15h-2v-2h2v2Zm0-4h-2V7h2v6Z"
        fill="currentColor"
      />
    </svg>`,
  loading: () => html`<svg class="spinner" width="50" height="50" viewBox="0 0 50 50" part="svg">
    <style>
      /* SVG spinner icon animation */
      .spinner {
        -webkit-animation: rotate 2s linear infinite;
        animation: rotate 2s linear infinite;
      }

      .spinner .path {
        stroke: currentColor;
        stroke-linecap: round;
        -webkit-animation: dash 1.5s ease-in-out infinite;
        animation: dash 1.5s ease-in-out infinite;
      }

      @-webkit-keyframes rotate {
        100% {
          -webkit-transform: rotate(360deg);
          transform: rotate(360deg);
        }
      }

      @keyframes rotate {
        100% {
          -webkit-transform: rotate(360deg);
          transform: rotate(360deg);
        }
      }

      @-webkit-keyframes dash {
        0% {
          stroke-dasharray: 1, 150;
          stroke-dashoffset: 0;
        }
        50% {
          stroke-dasharray: 90, 150;
          stroke-dashoffset: -35;
        }
        100% {
          stroke-dasharray: 90, 150;
          stroke-dashoffset: -124;
        }
      }

      @keyframes dash {
        0% {
          stroke-dasharray: 1, 150;
          stroke-dashoffset: 0;
        }
        50% {
          stroke-dasharray: 90, 150;
          stroke-dashoffset: -35;
        }
        100% {
          stroke-dasharray: 90, 150;
          stroke-dashoffset: -124;
        }
      }
    </style>
    <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5" />
  </svg>`,
  maximize: () => html`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" part="svg">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M6.66675 3.33331H3.33341H1.66675V4.99998V8.33331H3.33341V4.99998H6.66675V3.33331ZM6.66675 15H3.33341V11.6666H1.66675V15V16.6666H3.33341H6.66675V15ZM13.3334 3.33331H16.6667H18.3334V4.99998V8.33331H16.6667V4.99998H13.3334V3.33331ZM16.6667 15H13.3334V16.6666H16.6667H18.3334V15V11.6666H16.6667V15Z"
        fill="currentColor"
      />
    </svg>`,
  minimize: () => html`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" part="svg">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M5.00008 3.33331H6.66675V6.66665V8.33331H5.00008H1.66675V6.66665H5.00008V3.33331ZM5.00008 11.6666H1.66675V13.3333H5.00008V16.6666H6.66675V13.3333V11.6666H5.00008ZM15.0001 6.66665H18.3334V8.33331H15.0001H13.3334V6.66665V3.33331H15.0001V6.66665ZM18.3334 11.6666H15.0001H13.3334V13.3333V16.6666H15.0001V13.3333H18.3334V11.6666Z"
        fill="currentColor"
      />
    </svg>`,
  muted: () => html`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" part="svg">
      <path
        d="M13.75 10C13.75 8.525 12.9 7.25833 11.6667 6.64167V8.48333L13.7083 10.525C13.7333 10.3583 13.75 10.1833 13.75 10ZM15.8333 10C15.8333 10.7833 15.6667 11.5167 15.3833 12.2L16.6417 13.4583C17.1917 12.425 17.5 11.25 17.5 10C17.5 6.43333 15.0083 3.45 11.6667 2.69167V4.40833C14.075 5.125 15.8333 7.35833 15.8333 10ZM3.55833 2.5L2.5 3.55833L6.44167 7.5H2.5V12.5H5.83333L10 16.6667V11.0583L13.5417 14.6C12.9833 15.0333 12.3583 15.375 11.6667 15.5833V17.3C12.8167 17.0417 13.8583 16.5083 14.7417 15.7917L16.4417 17.5L17.5 16.4417L10 8.94167L3.55833 2.5ZM10 3.33333L8.25833 5.075L10 6.81667V3.33333Z"
        fill="currentColor"
      />
    </svg>`,
  pause: () => html`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" part="svg">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M8.33333 2.5H5V17.5H8.33333V2.5ZM15 2.5H11.6667V17.5H15V2.5Z"
        fill="currentColor"
      />
    </svg>`,
  "play-large": () => html`<svg width="45" height="53" viewBox="0 0 45 53" fill="none" part="svg">
      <path
        d="M43.5 23.8319C45.5 25.0083 45.5 27.9492 43.5 29.1256L4.5 52.0647C2.5 53.2411 0 51.7706 0 49.4179V3.53961C0 1.18687 2.5 -0.283582 4.5 0.892785L43.5 23.8319Z"
        fill="currentColor"
      />
    </svg>`,
  play: () => html`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" part="svg">
      <path d="M17.3803 9.99998L5.83325 16.6666V3.33331L17.3803 9.99998Z" fill="currentColor" />
    </svg>`,
  settings: () => html`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" part="svg">
      <path
        d="M7.70833 18.3334L7.375 15.6667C7.19445 15.5972 7.02444 15.5139 6.865 15.4167C6.705 15.3195 6.54861 15.2153 6.39583 15.1042L3.91667 16.1459L1.625 12.1875L3.77083 10.5625C3.75694 10.4653 3.75 10.3714 3.75 10.2809C3.75 10.1909 3.75 10.0972 3.75 10C3.75 9.9028 3.75 9.80891 3.75 9.71835C3.75 9.62835 3.75694 9.53474 3.77083 9.43752L1.625 7.81252L3.91667 3.85419L6.39583 4.89585C6.54861 4.78474 6.70833 4.68058 6.875 4.58335C7.04167 4.48613 7.20833 4.4028 7.375 4.33335L7.70833 1.66669H12.2917L12.625 4.33335C12.8056 4.4028 12.9758 4.48613 13.1358 4.58335C13.2953 4.68058 13.4514 4.78474 13.6042 4.89585L16.0833 3.85419L18.375 7.81252L16.2292 9.43752C16.2431 9.53474 16.25 9.62835 16.25 9.71835C16.25 9.80891 16.25 9.9028 16.25 10C16.25 10.0972 16.25 10.1909 16.25 10.2809C16.25 10.3714 16.2361 10.4653 16.2083 10.5625L18.3542 12.1875L16.0625 16.1459L13.6042 15.1042C13.4514 15.2153 13.2917 15.3195 13.125 15.4167C12.9583 15.5139 12.7917 15.5972 12.625 15.6667L12.2917 18.3334H7.70833ZM10.0417 12.9167C10.8472 12.9167 11.5347 12.632 12.1042 12.0625C12.6736 11.4931 12.9583 10.8056 12.9583 10C12.9583 9.19447 12.6736 8.50696 12.1042 7.93752C11.5347 7.36808 10.8472 7.08335 10.0417 7.08335C9.22222 7.08335 8.53111 7.36808 7.96833 7.93752C7.40611 8.50696 7.125 9.19447 7.125 10C7.125 10.8056 7.40611 11.4931 7.96833 12.0625C8.53111 12.632 9.22222 12.9167 10.0417 12.9167ZM10.0417 11.25C9.69445 11.25 9.39945 11.1284 9.15667 10.885C8.91333 10.6422 8.79167 10.3472 8.79167 10C8.79167 9.6528 8.91333 9.3578 9.15667 9.11502C9.39945 8.87169 9.69445 8.75002 10.0417 8.75002C10.3889 8.75002 10.6842 8.87169 10.9275 9.11502C11.1703 9.3578 11.2917 9.6528 11.2917 10C11.2917 10.3472 11.1703 10.6422 10.9275 10.885C10.6842 11.1284 10.3889 11.25 10.0417 11.25ZM9.16667 16.6667H10.8125L11.1042 14.4584C11.5347 14.3472 11.9342 14.1839 12.3025 13.9684C12.6703 13.7534 13.0069 13.4931 13.3125 13.1875L15.375 14.0417L16.1875 12.625L14.3958 11.2709C14.4653 11.0764 14.5139 10.8714 14.5417 10.6559C14.5694 10.4409 14.5833 10.2222 14.5833 10C14.5833 9.7778 14.5694 9.55891 14.5417 9.34335C14.5139 9.12835 14.4653 8.92363 14.3958 8.72919L16.1875 7.37502L15.375 5.95835L13.3125 6.83335C13.0069 6.51391 12.6703 6.24641 12.3025 6.03085C11.9342 5.81585 11.5347 5.6528 11.1042 5.54169L10.8333 3.33335H9.1875L8.89583 5.54169C8.46528 5.6528 8.06611 5.81585 7.69833 6.03085C7.33 6.24641 6.99306 6.50697 6.6875 6.81252L4.625 5.95835L3.8125 7.37502L5.60417 8.70835C5.53472 8.91669 5.48611 9.12502 5.45833 9.33335C5.43056 9.54169 5.41667 9.76391 5.41667 10C5.41667 10.2222 5.43056 10.4375 5.45833 10.6459C5.48611 10.8542 5.53472 11.0625 5.60417 11.2709L3.8125 12.625L4.625 14.0417L6.6875 13.1667C6.99306 13.4861 7.33 13.7534 7.69833 13.9684C8.06611 14.1839 8.46528 14.3472 8.89583 14.4584L9.16667 16.6667Z"
        fill="currentColor"
      />
    </svg>`,
  tick: () => html`<svg width="20" height="21" viewBox="0 0 20 21" fill="none" part="svg">
      <path
        d="M7.50016 14.0001L4.00016 10.5001L2.8335 11.6667L7.50016 16.3334L17.5002 6.33341L16.3335 5.16675L7.50016 14.0001Z"
        fill="currentColor"
      />
    </svg>`,
  "volume-high": () => html`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" part="svg">
      <path
        d="M2.5 7.49998V12.5H5.83333L10 16.6667V3.33332L5.83333 7.49998H2.5ZM13.75 9.99998C13.75 8.52498 12.9 7.25832 11.6667 6.64165V13.35C12.9 12.7417 13.75 11.475 13.75 9.99998ZM11.6667 2.69165V4.40832C14.075 5.12498 15.8333 7.35832 15.8333 9.99998C15.8333 12.6417 14.075 14.875 11.6667 15.5917V17.3083C15.0083 16.55 17.5 13.5667 17.5 9.99998C17.5 6.43332 15.0083 3.44998 11.6667 2.69165Z"
        fill="currentColor"
      />
    </svg>`,
  "volume-low": () => html`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" part="svg">
      <path d="M5.83325 7.49998V12.5H9.16658L13.3333 16.6666V3.33331L9.16658 7.49998H5.83325Z" fill="currentColor" />
    </svg>`,
  "volume-medium": () => html`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" part="svg">
      <path
        d="M15.4167 9.99998C15.4167 8.52498 14.5667 7.25831 13.3334 6.64165V13.35C14.5667 12.7416 15.4167 11.475 15.4167 9.99998ZM4.16675 7.49998V12.5H7.50008L11.6667 16.6666V3.33331L7.50008 7.49998H4.16675Z"
        fill="currentColor"
      />
    </svg>`,
  vouch: () => html`<svg
      xmlns="http://www.w3.org/2000/svg"
      height="28"
      width="105"
      viewBox="0 0 105 28"
      fill="currentColor"
      part="svg"
    >
      <path
        d="M9.853 23.246L4.86 7.688H0L7 27.42h5.52l7.265-19.733h-4.65L9.854 23.246zM95.12 7.08a8.905 8.905 0 00-4.648 1.294V0H86.14v27.419h4.332V16.087a4.656 4.656 0 014.649-4.65 4.656 4.656 0 014.649 4.65v11.332h4.332V16.087c.026-4.966-4.015-9.008-8.981-9.008zM57.188 19.02a4.656 4.656 0 01-4.649 4.649 4.656 4.656 0 01-4.649-4.65V7.689h-4.332V19.02c0 4.966 4.041 8.98 8.981 8.98 4.94 0 8.981-4.04 8.981-8.98V7.688h-4.332V19.02zM30.483 7.133c-5.732 0-10.408 4.675-10.408 10.407s4.676 10.408 10.408 10.408c5.732 0 10.408-4.676 10.408-10.408 0-5.732-4.676-10.407-10.408-10.407zm0 16.483a6.076 6.076 0 116.075-6.076c0 3.355-2.747 6.076-6.075 6.076zM74.517 23.616a6.076 6.076 0 010-12.151 6.132 6.132 0 015.045 2.668l3.592-2.43a10.473 10.473 0 00-8.637-4.57c-5.732 0-10.408 4.675-10.408 10.407s4.676 10.408 10.408 10.408a10.35 10.35 0 008.611-4.596l-3.593-2.43a5.975 5.975 0 01-5.018 2.694z"
      />
    </svg>`
};
var __defProp$c = Object.defineProperty;
var __getOwnPropDesc$c = Object.getOwnPropertyDescriptor;
var __decorateClass$c = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$c(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$c(target, key, result);
  return result;
};
let Icon = class extends ThemedLitElement {
  render() {
    if (!this.icon || !Object.keys(icons).includes(this.icon)) {
      return null;
    }
    return icons[this.icon]();
  }
};
Icon.styles = [
  ThemedLitElement.styles ?? [],
  css`
      :host {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }
    `
];
__decorateClass$c([
  property({ type: String })
], Icon.prototype, "icon", 2);
Icon = __decorateClass$c([
  customElement("vmp-icon")
], Icon);
var __defProp$b = Object.defineProperty;
var __getOwnPropDesc$b = Object.getOwnPropertyDescriptor;
var __decorateClass$b = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$b(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$b(target, key, result);
  return result;
};
let IconButton = class extends ThemedLitElement {
  constructor() {
    super(...arguments);
    this.active = false;
    this.size = "small";
    this.rounded = "medium";
    this.weight = "light";
  }
  render() {
    return html`
      <vmp-button
        ?active=${this.active}
        size=${ifDefined(this.size)}
        rounded=${ifDefined(this.rounded)}
        weight=${ifDefined(this.weight)}
      >
        <vmp-icon
          class=${classMap({
      tiny: this.size === "tiny",
      small: this.size === "small",
      large: this.size === "large"
    })}
          icon=${ifDefined(this.icon)}
        ></vmp-icon>
      </vmp-button>
    `;
  }
};
IconButton.styles = [
  ThemedLitElement.styles ?? [],
  css`
      .tiny::part(svg) {
        height: 12px;
        width: 12px;
      }

      .small::part(svg) {
        height: 20px;
        width: 20px;
      }

      .large::part(svg) {
        height: 32px;
        width: 32px;
      }
    `
];
__decorateClass$b([
  property({ type: Boolean })
], IconButton.prototype, "active", 2);
__decorateClass$b([
  property({ type: String })
], IconButton.prototype, "size", 2);
__decorateClass$b([
  property({ type: String })
], IconButton.prototype, "rounded", 2);
__decorateClass$b([
  property({ type: String })
], IconButton.prototype, "weight", 2);
__decorateClass$b([
  property({ type: String })
], IconButton.prototype, "icon", 2);
IconButton = __decorateClass$b([
  customElement("vmp-icon-button")
], IconButton);
var __defProp$a = Object.defineProperty;
var __getOwnPropDesc$a = Object.getOwnPropertyDescriptor;
var __decorateClass$a = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$a(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$a(target, key, result);
  return result;
};
let VideoPreviewContent = class extends ThemedLitElement {
  constructor() {
    super(...arguments);
    this.disabled = false;
    this.aspectRatio = 1;
    this.compilation = crossFade;
    this._currentTime = 0;
    this._sceneController = new SceneController2(this);
    this._canvasRef = createRef();
    this._previewRef = createRef();
    this._previewTimeRef = createRef();
    this._handleScrubberMouseEnter = ({ detail: { x, y, elementLeft, elementRight } }) => {
      var _a, _b;
      if ((_a = this._previewRef) == null ? void 0 : _a.value) {
        const previewRect = this._previewRef.value.getBoundingClientRect();
        const parentRect = (_b = this._previewRef.value.getRootNode().host) == null ? void 0 : _b.getBoundingClientRect();
        if (previewRect && parentRect) {
          this._previewRef.value.style.opacity = "1";
          this._previewRef.value.style.transform = `translate3d(${clamp(
            elementLeft - parentRect.left,
            x - parentRect.left - previewRect.width / 2,
            elementRight - parentRect.left - previewRect.width
          )}px, ${y - parentRect.top - previewRect.height - 28}px, 0)`;
        }
      }
    };
    this._handleScrubberMouseMove = ({ detail: { time, x, elementLeft, elementRight } }) => {
      var _a, _b, _c, _d, _e, _f, _g, _h;
      this._currentTime = time;
      if ((_a = this._sceneController.scenes) == null ? void 0 : _a.scenes.length) {
        const activeVideo = (_e = (_d = (_c = (_b = this._sceneController.scenes) == null ? void 0 : _b.scenes) == null ? void 0 : _c.find(({ start, duration, video }) => {
          return video && time >= start && time <= start + duration;
        })) == null ? void 0 : _d.video) == null ? void 0 : _e.node;
        if (activeVideo) {
          activeVideo.currentTime = time;
        }
        this._draw();
        if ((_f = this._previewRef) == null ? void 0 : _f.value) {
          const previewRect = this._previewRef.value.getBoundingClientRect();
          const parentRect = (_g = this._previewRef.value.getRootNode().host) == null ? void 0 : _g.getBoundingClientRect();
          if (previewRect && parentRect) {
            const transform = this._previewRef.value.style.transform || "translate3d(0px, 0px, 0px)";
            const [, y] = Array.from(transform.matchAll(/([+-]?([0-9]*[.])?[0-9]+)(?=px)\w+/g));
            this._previewRef.value.style.transform = `translate3d(${clamp(
              elementLeft - parentRect.left,
              x - parentRect.left - previewRect.width / 2,
              elementRight - parentRect.left - previewRect.width
            )}px, ${y[1]}px, 0)`;
          }
        }
        if (this._previewTimeRef.value) {
          this._previewTimeRef.value.setAttribute(
            "content",
            formatDuration(Math.max(0, Math.min(this._currentTime, (_h = this._sceneController.scenes) == null ? void 0 : _h.duration)))
          );
        }
      }
    };
    this._handleScrubberMouseLeave = () => {
      var _a;
      if ((_a = this._previewRef) == null ? void 0 : _a.value) {
        this._previewRef.value.style.opacity = "0";
      }
    };
    this._handleScrubberResize = ({ detail: { y } }) => {
      var _a, _b;
      if ((_a = this._previewRef) == null ? void 0 : _a.value) {
        const previewRect = this._previewRef.value.getBoundingClientRect();
        const parentRect = (_b = this._previewRef.value.getRootNode().host) == null ? void 0 : _b.getBoundingClientRect();
        if (previewRect && parentRect) {
          const transform = this._previewRef.value.style.transform || "translate3d(0px, 0px, 0px)";
          const [x] = Array.from(transform.matchAll(/([+-]?([0-9]*[.])?[0-9]+)(?=px)\w+/g));
          this._previewRef.value.style.transform = `translate3d(${x[1]}px, ${y - parentRect.top - previewRect.height - 28}px, 0)`;
        }
      }
    };
  }
  _draw(time = this._currentTime) {
    var _a, _b;
    if (this._sceneController.scenes && this._canvasRef.value) {
      (_b = this.compilation) == null ? void 0 : _b.call(this, {
        currentTime: Math.max(0, Math.min(time, (_a = this._sceneController.scenes) == null ? void 0 : _a.duration)),
        scenes: this._sceneController.scenes,
        canvas: this._canvasRef.value
      });
    }
  }
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("scrubber:resize", this._handleScrubberResize);
    document.addEventListener("scrubber:mouseenter", this._handleScrubberMouseEnter);
    document.addEventListener("scrubber:mousemove", this._handleScrubberMouseMove);
    document.addEventListener("scrubber:mouseleave", this._handleScrubberMouseLeave);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("scrubber:resize", this._handleScrubberResize);
    document.removeEventListener("scrubber:mouseenter", this._handleScrubberMouseEnter);
    document.removeEventListener("scrubber:mousemove", this._handleScrubberMouseMove);
    document.removeEventListener("scrubber:mouseleave", this._handleScrubberMouseLeave);
  }
  render() {
    var _a;
    const { height, width } = getSizeDetails({
      resolution: 180,
      aspectRatio: this.aspectRatio
    });
    const sceneLength = ((_a = this._sceneController.scenes) == null ? void 0 : _a.scenes.length) ?? 0;
    return !this.disabled && (!!this.src || sceneLength > 1) ? html`
          <div
            ${ref(this._previewRef)}
            class="preview"
            style=${styleMap({
      width: this.aspectRatio > 1 ? "140px" : void 0,
      height: this.aspectRatio <= 1 ? "140px" : void 0,
      aspectRatio: this.aspectRatio
    })}
          >
            <canvas ${ref(this._canvasRef)} class="preview-canvas" width=${width} height=${height}></canvas>
            <vmp-text ${ref(this._previewTimeRef)} class="preview-text" size="small"></vmp-text>
          </div>
        ` : null;
  }
};
VideoPreviewContent.styles = [
  ThemedLitElement.styles ?? [],
  css`
      .preview {
        position: absolute;
        display: flex;
        top: 0;
        left: 0;
        align-items: center;
        flex-direction: column;
        opacity: 0;
        // Always on top
        z-index: 2147483647;
        pointer-events: none;
        border: 3px solid var(--colors-light-1000);
        background: var(--colors-dark-1000);
        border-radius: var(--radii-medium);
        transition: opacity 150ms ease-out;
      }

      .preview-canvas {
        width: 100%;
        height: 100%;
        border-radius: var(--radii-small);
      }

      .preview-text {
        position: absolute;
        bottom: 0;
        transform: translate3d(0, calc(100% + 9px), 0);
        color: var(--colors-light-1000);
        font-variant-numeric: tabular-nums;
      }
    `
];
__decorateClass$a([
  property({ type: String })
], VideoPreviewContent.prototype, "src", 2);
__decorateClass$a([
  property({ type: Object })
], VideoPreviewContent.prototype, "data", 2);
__decorateClass$a([
  property({ type: Object })
], VideoPreviewContent.prototype, "template", 2);
__decorateClass$a([
  property({ type: Boolean })
], VideoPreviewContent.prototype, "disabled", 2);
__decorateClass$a([
  property({ type: Number })
], VideoPreviewContent.prototype, "aspectRatio", 2);
__decorateClass$a([
  property({ attribute: false })
], VideoPreviewContent.prototype, "compilation", 2);
VideoPreviewContent = __decorateClass$a([
  customElement("vmp-video-preview-content")
], VideoPreviewContent);
let VideoPreview = class extends ThemedLitElement {
  constructor() {
    super(...arguments);
    this.disabled = false;
    this.aspectRatio = 1;
    this.compilation = crossFade;
  }
  render() {
    return html`
      <vmp-video-preview-content
        ?disabled=${this.disabled}
        .src=${this.src}
        .data=${this.data}
        .template=${this.template}
        .aspectRatio=${this.aspectRatio}
        .compilation=${this.compilation}
      ></vmp-video-preview-content>
    `;
  }
};
__decorateClass$a([
  property({ type: String })
], VideoPreview.prototype, "src", 2);
__decorateClass$a([
  property({ type: Object })
], VideoPreview.prototype, "data", 2);
__decorateClass$a([
  property({ type: Object })
], VideoPreview.prototype, "template", 2);
__decorateClass$a([
  property({ type: Boolean })
], VideoPreview.prototype, "disabled", 2);
__decorateClass$a([
  property({ type: Number })
], VideoPreview.prototype, "aspectRatio", 2);
__decorateClass$a([
  property({ attribute: false })
], VideoPreview.prototype, "compilation", 2);
VideoPreview = __decorateClass$a([
  customElement("vmp-video-preview")
], VideoPreview);
class RegisterElementDirective extends Directive {
  constructor(partInfo) {
    super(partInfo);
    if (partInfo.type !== PartType.ELEMENT) {
      throw new Error("Resize directive must be used in an Element expression");
    }
  }
  update(part, [callback]) {
    callback == null ? void 0 : callback(part.element);
    return noChange;
  }
}
const registerElementDirective = directive(RegisterElementDirective);
class ResizeHider {
  constructor(host) {
    this.hiddenControls = [];
    this._measureRef = createRef();
    this._prevControlsWidth = 0;
    this._hideables = {};
    this._showables = {};
    this._onResize = (entries) => {
      var _a, _b, _c;
      const target = this._measureRef.value;
      const width = (_c = (_b = (_a = entries == null ? void 0 : entries[0]) == null ? void 0 : _a.borderBoxSize) == null ? void 0 : _b[0]) == null ? void 0 : _c.inlineSize;
      if (target && width && width !== this._prevControlsWidth) {
        Object.keys(this._hideables).forEach((key) => {
          this._hideables[key].element.style.display = "";
        });
        Object.keys(this._showables).forEach((key) => {
          this._showables[key].element.style.display = "none";
        });
        const controlsToHide = [];
        while (target.scrollWidth > target.offsetWidth) {
          const nextControl = Object.keys(this._hideables).find(
            (key) => this._hideables[key].hideIndex === controlsToHide.length
          );
          if (!nextControl) {
            break;
          }
          const nextNode = this._hideables[nextControl].element;
          if (nextNode) {
            nextNode.style.display = "none";
          }
          controlsToHide.push(nextControl);
          Object.keys(this._showables).forEach((key) => {
            if (this._showables[key].showOn.some((control) => controlsToHide.includes(control))) {
              this._showables[key].element.style.display = "flex";
            }
          });
        }
        this.hiddenControls = controlsToHide;
        this._prevControlsWidth = width;
        this.host.requestUpdate();
      }
    };
    this.host = host;
  }
  measure() {
    return ref(this._measureRef);
  }
  contain() {
    return resizeObserver(this._onResize);
  }
  hide(key, hideIndex) {
    return registerElementDirective((element) => {
      this._hideables[key] = { element, hideIndex };
    });
  }
  show(key, showOn) {
    return registerElementDirective((element) => {
      this._showables[key] = { element, showOn };
    });
  }
}
const tippyCss = ".tippy-box[data-animation=shift-away-subtle][data-state=hidden]{opacity:0}.tippy-box[data-animation=shift-away-subtle][data-state=hidden][data-placement^=top]{transform:translateY(5px)}.tippy-box[data-animation=shift-away-subtle][data-state=hidden][data-placement^=bottom]{transform:translateY(-5px)}.tippy-box[data-animation=shift-away-subtle][data-state=hidden][data-placement^=left]{transform:translateX(5px)}.tippy-box[data-animation=shift-away-subtle][data-state=hidden][data-placement^=right]{transform:translateX(-5px)}";
var __defProp$9 = Object.defineProperty;
var __getOwnPropDesc$9 = Object.getOwnPropertyDescriptor;
var __decorateClass$9 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$9(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$9(target, key, result);
  return result;
};
let Text = class extends ThemedLitElement {
  constructor() {
    super(...arguments);
    this.semibold = false;
    this.truncate = false;
    this.size = "medium";
    this.content = "";
  }
  render() {
    return html`
      <div
        part="content"
        class=${classMap({
      semibold: this.semibold,
      truncate: this.truncate,
      [this.size]: true
    })}
        style=${styleMap({
      fontFamily: appendFontFamilies({ text: this.content, font: "var(--fontFamilies-sans)" })
    })}
      >
        ${this.content}
      </div>
    `;
  }
};
Text.styles = [
  ThemedLitElement.styles ?? [],
  css`
      :host {
        --text-size: var(--fontSizes-medium);
        --text-line-height: var(--lineHeights-medium);
        --text-weight: var(--fontWeights-regular);
      }

      div {
        font-size: var(--text-size);
        line-height: var(--text-line-height);
        font-weight: var(--text-weight);
      }

      .small {
        --text-size: var(--fontSizes-small);
        --text-line-height: var(--lineHeights-small);
      }

      .medium {
        --text-size: var(--fontSizes-medium);
        --text-line-height: var(--lineHeights-medium);
      }

      .large {
        --text-size: var(--fontSizes-large);
        --text-line-height: var(--lineHeights-large);
      }

      .semibold {
        --text-weight: var(--fontWeights-semibold);
      }

      .truncate {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    `
];
__decorateClass$9([
  property({ type: Boolean })
], Text.prototype, "semibold", 2);
__decorateClass$9([
  property({ type: Boolean })
], Text.prototype, "truncate", 2);
__decorateClass$9([
  property({ type: String })
], Text.prototype, "size", 2);
__decorateClass$9([
  property({ type: String })
], Text.prototype, "content", 2);
Text = __decorateClass$9([
  customElement("vmp-text")
], Text);
var __defProp$8 = Object.defineProperty;
var __getOwnPropDesc$8 = Object.getOwnPropertyDescriptor;
var __decorateClass$8 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$8(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$8(target, key, result);
  return result;
};
let Menu = class extends ThemedLitElement {
  constructor() {
    super(...arguments);
    this.activeItems = [];
    this.visibleItems = null;
    this.subItemsActive = null;
    this.tippy = null;
    this._contentRef = createRef();
  }
  _handleBackClick() {
    this.subItemsActive = null;
    this.visibleItems = this.items ?? null;
  }
  willUpdate() {
    this.visibleItems = this.visibleItems ?? this.items ?? null;
  }
  updated() {
    var _a, _b;
    const subItems = (_b = (_a = this.items) == null ? void 0 : _a.items.find(({ id }) => this.subItemsActive === id)) == null ? void 0 : _b.subItems;
    this.visibleItems = subItems ?? this.items ?? null;
    if (!subItems) {
      this.subItemsActive = null;
    }
  }
  connectedCallback() {
    super.connectedCallback();
    requestAnimationFrame(() => {
      var _a;
      const slot = (_a = this.shadowRoot) == null ? void 0 : _a.querySelector("slot");
      const anchor = slot == null ? void 0 : slot.assignedElements()[0];
      if (anchor && this._contentRef.value) {
        this.tippy = tippy(anchor, {
          content: this._contentRef.value,
          allowHTML: true,
          arrow: false,
          animation: "shift-away-subtle",
          interactive: true,
          placement: "top",
          trigger: "click",
          onShow: () => {
            this.dispatchEvent(new CustomEvent("menu:toggle", { detail: true }));
          },
          onHide: () => {
            this.dispatchEvent(new CustomEvent("menu:toggle", { detail: false }));
          },
          appendTo: () => this.shadowRoot
        });
      }
    });
  }
  disconnectedCallback() {
    var _a;
    super.disconnectedCallback();
    (_a = this.tippy) == null ? void 0 : _a.destroy();
  }
  render() {
    var _a, _b;
    return html`
      <slot></slot>
      <div ${ref(this._contentRef)} class="content">
        <div class="title">
          ${this.subItemsActive ? html`
                <button class="back" @click=${this._handleBackClick}>
                  <vmp-icon icon="chevron-down"></vmp-icon>
                </button>
              ` : null}
          <vmp-text
            ?bold=${true}
            ?truncate=${true}
            size="small"
            .content=${((_a = this.visibleItems) == null ? void 0 : _a.title) ?? "Sub Menu"}
          ></vmp-text>
        </div>
        ${repeat(
      ((_b = this.visibleItems) == null ? void 0 : _b.items) ?? [],
      ({ id }) => id,
      (item) => {
        return html`
              <button
                class="item"
                @click=${() => {
          if (item.subItems) {
            this.subItemsActive = item.id;
            this.visibleItems = item.subItems;
          }
          if (item.event) {
            this.dispatchEvent(new CustomEvent(item.event, { detail: item, bubbles: true, composed: true }));
          }
        }}
              >
                <vmp-text ?semibold=${true} size="small" content=${item.label}></vmp-text>
                ${item.subItems ? html`<vmp-icon class="forward" icon="chevron-down"></vmp-icon>` : this.activeItems.includes(item.id) ? html`<vmp-icon icon="tick"></vmp-icon>` : null}
              </button>
            `;
      }
    )}
      </div>
    `;
  }
};
Menu.styles = [
  ThemedLitElement.styles ?? [],
  unsafeCSS(tippyCss),
  css`
      :host {
        display: flex;
      }

      .content {
        display: flex;
        min-width: 140px;
        overflow: hidden;
        flex-direction: column;
        color: var(--colors-dark-1000);
        background: var(--colors-light-1000);
        border-radius: var(--radii-medium);
        transition: height 150ms ease-out;
      }

      .back {
        padding: 0;
        margin: 0;
        border: 0;
        cursor: pointer;
        background: transparent;
        color: var(--colors-dark-400);
        /* TODO: Kinda eyeballing this one */
        margin-left: calc(var(--space-xxs) * -1);
        transform: rotate(90deg);
      }

      .forward {
        transform: rotate(-90deg);
      }

      .title {
        display: flex;
        align-items: center;
        gap: var(--space-xxs);
        color: var(--colors-dark-400);
        text-transform: uppercase;
        height: 36px;
        padding: var(--space-small) var(--space-small) var(--space-xxs) var(--space-small);
      }

      .item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 42px;
        border: none;
        cursor: pointer;
        color: var(--colors-dark-1000);
        background: transparent;
        padding: 0 var(--space-small);
      }

      .item:hover {
        background: var(--colors-dark-100);
      }

      vmp-icon::part(svg) {
        height: 20px;
        width: 20px;
      }
    `
];
__decorateClass$8([
  property({ type: Object })
], Menu.prototype, "items", 2);
__decorateClass$8([
  property({ type: Array })
], Menu.prototype, "activeItems", 2);
__decorateClass$8([
  state()
], Menu.prototype, "visibleItems", 2);
__decorateClass$8([
  state()
], Menu.prototype, "subItemsActive", 2);
Menu = __decorateClass$8([
  customElement("vmp-menu")
], Menu);
var __defProp$7 = Object.defineProperty;
var __getOwnPropDesc$7 = Object.getOwnPropertyDescriptor;
var __decorateClass$7 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$7(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$7(target, key, result);
  return result;
};
let Tooltip = class extends ThemedLitElement {
  constructor() {
    super(...arguments);
    this.content = "";
    this.disabled = false;
    this.tippy = null;
    this._contentRef = createRef();
  }
  connectedCallback() {
    super.connectedCallback();
    requestAnimationFrame(() => {
      var _a;
      const slot = (_a = this.shadowRoot) == null ? void 0 : _a.querySelector("slot");
      const anchor = slot == null ? void 0 : slot.assignedElements()[0];
      if (anchor && this._contentRef.value) {
        this.tippy = tippy(anchor, {
          content: this._contentRef.value,
          allowHTML: true,
          arrow: false,
          animation: "shift-away-subtle",
          interactive: false,
          placement: "top",
          // To be above the vmp-menu
          zIndex: 1e4,
          appendTo: () => this.shadowRoot
        });
      }
    });
  }
  disconnectedCallback() {
    var _a;
    super.disconnectedCallback();
    (_a = this.tippy) == null ? void 0 : _a.destroy();
  }
  updated() {
    var _a, _b;
    if (this.disabled) {
      (_a = this.tippy) == null ? void 0 : _a.disable();
    } else {
      (_b = this.tippy) == null ? void 0 : _b.enable();
    }
  }
  render() {
    return html`
      <slot></slot>
      <div ${ref(this._contentRef)} class="content">
        <vmp-text content=${this.content}></vmp-text>
      </div>
    `;
  }
};
Tooltip.styles = [
  ThemedLitElement.styles ?? [],
  unsafeCSS(tippyCss),
  css`
      :host {
        display: flex;
      }

      .content {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--space-small);
        color: var(--colors-light-1000);
        background: var(--colors-dark-800);
        border-radius: var(--radii-medium);
      }
    `
];
__decorateClass$7([
  property({ type: String })
], Tooltip.prototype, "content", 2);
__decorateClass$7([
  property({ type: Boolean })
], Tooltip.prototype, "disabled", 2);
Tooltip = __decorateClass$7([
  customElement("vmp-tooltip")
], Tooltip);
var __defProp$6 = Object.defineProperty;
var __getOwnPropDesc$6 = Object.getOwnPropertyDescriptor;
var __decorateClass$6 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$6(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$6(target, key, result);
  return result;
};
let VolumeButton = class extends ThemedLitElement {
  constructor() {
    super(...arguments);
    this.volume = 1;
    this.muted = false;
    this.tippy = null;
    this._contentRef = createRef();
    this._anchorRef = createRef();
    this._menuOpen = false;
  }
  connectedCallback() {
    super.connectedCallback();
    requestAnimationFrame(() => {
      if (this._anchorRef.value && this._contentRef.value) {
        this.tippy = tippy(this._anchorRef.value, {
          content: this._contentRef.value,
          allowHTML: true,
          arrow: false,
          animation: "shift-away-subtle",
          interactive: true,
          placement: "top",
          interactiveBorder: 20,
          onShow: () => {
            this._menuOpen = true;
            this.dispatchEvent(new CustomEvent("menu:toggle", { detail: true }));
          },
          onHide: () => {
            this._menuOpen = false;
            this.dispatchEvent(new CustomEvent("menu:toggle", { detail: false }));
          },
          appendTo: () => this.shadowRoot
        });
      }
    });
  }
  disconnectedCallback() {
    var _a;
    super.disconnectedCallback();
    (_a = this.tippy) == null ? void 0 : _a.destroy();
  }
  render() {
    return html`
      <vmp-icon-button
        ${ref(this._anchorRef)}
        icon=${this.muted || this.volume === 0 ? "muted" : this.volume < 1 / 3 ? "volume-low" : this.volume < 2 / 3 ? "volume-medium" : "volume-high"}
        weight="heavy"
        rounded="full"
        @pointerdown=${(e) => {
      if (this._menuOpen) {
        e.preventDefault();
      }
    }}
        @click=${() => {
      if (this._menuOpen) {
        this.dispatchEvent(new CustomEvent("volume:click", { bubbles: true, composed: true, detail: this.muted }));
      }
    }}
      ></vmp-icon-button>
      <div ${ref(this._contentRef)} class="content">
        <input
          key="volume"
          type="range"
          min="0"
          max="1"
          step="0.01"
          .value=${this.volume.toString()}
          @blur=${() => {
      var _a;
      return (_a = this.tippy) == null ? void 0 : _a.hide();
    }}
        />
      </div>
    `;
  }
};
VolumeButton.styles = [
  ThemedLitElement.styles ?? [],
  unsafeCSS(tippyCss),
  css`
      .content {
        --volume-slider-height: 4px;
        --volume-slider-width: 78px;

        display: flex;
        height: var(--volume-slider-width);
        width: var(--volume-slider-height);
      }

      .spacer {
        display: flex;
        height: var(--volume-slider-width);
        width: var(--volume-slider-height);
      }

      input {
        position: absolute;
        appearance: none !important;
        height: var(--volume-slider-height);
        width: var(--volume-slider-width);
        overflow: hidden;
        border-radius: 1000rem;
        cursor: pointer;
        background: var(--colors-light-200);
        rotate: -90deg;
        /* Minus the input margin */
        translate: 0 calc(var(--volume-slider-width) - 4px);
        transform-origin: left;
        transition: height 100ms ease-out, transform 100ms ease-out;
      }

      input:hover {
        height: calc(var(--volume-slider-height) * 2);
      }

      input::-webkit-slider-runnable-track {
        appearance: none !important;
        height: var(--volume-slider-height);
        color: var(--colors-light-1000);
        transition: height 100ms ease-out;
      }

      input::-moz-range-track {
        appearance: none !important;
        height: var(--volume-slider-height);
        color: var(--colors-light-1000);
        transition: height 100ms ease-out;
      }

      input::-ms-track {
        appearance: none !important;
        height: var(--volume-slider-height);
        color: var(--colors-light-1000);
        transition: height 100ms ease-out;
      }

      input:hover::-webkit-slider-runnable-track {
        height: calc(var(--volume-slider-height) * 2);
      }

      input:hover::-moz-range-track {
        height: calc(var(--volume-slider-height) * 2);
      }

      input:hover::-ms-track {
        height: calc(var(--volume-slider-height) * 2);
      }

      input::-webkit-slider-thumb {
        appearance: none !important;
        height: var(--volume-slider-height) !important;
        width: var(--volume-slider-height) !important;
        color: var(--colors-light-1000);
        border-radius: 50%;
        border: none;
        background: var(--colors-light-1000);
        box-shadow: calc((var(--volume-slider-width) + var(--volume-slider-height) / 2) * -1) 0 0
            var(--volume-slider-width),
          calc((var(--volume-slider-width) + var(--volume-slider-height) / 2)) 0 0 var(--volume-slider-width)
            var(--colors-dark-640);
        transition: height 100ms ease-out, width 100ms ease-out, box-shadow 100ms ease-out, color 200ms ease-out;
      }

      input::-moz-range-thumb {
        appearance: none !important;
        height: var(--volume-slider-height) !important;
        width: var(--volume-slider-height) !important;
        color: var(--colors-light-1000);
        border-radius: 50%;
        border: none;
        background: var(--colors-light-1000);
        box-shadow: calc((var(--volume-slider-width) + var(--volume-slider-height) / 2) * -1) 0 0
            var(--volume-slider-width),
          calc((var(--volume-slider-width) + var(--volume-slider-height) / 2)) 0 0 var(--volume-slider-width)
            var(--colors-dark-640);
        transition: height 100ms ease-out, width 100ms ease-out, box-shadow 100ms ease-out, color 200ms ease-out;
      }

      input::-ms-thumb {
        appearance: none !important;
        height: var(--volume-slider-height) !important;
        width: var(--volume-slider-height) !important;
        color: var(--colors-light-1000);
        border-radius: 50%;
        border: none;
        background: var(--colors-light-1000);
        box-shadow: calc((var(--volume-slider-width) + var(--volume-slider-height) / 2) * -1) 0 0
            var(--volume-slider-width),
          calc((var(--volume-slider-width) + var(--volume-slider-height) / 2)) 0 0 var(--volume-slider-width)
            var(--colors-dark-640);
        transition: height 100ms ease-out, width 100ms ease-out, box-shadow 100ms ease-out, color 200ms ease-out;
      }

      input:hover::-webkit-slider-thumb {
        --volume-slider-height: 8px;
        color: var(--colors-light-800);
      }

      input:hover::-moz-range-thumb {
        --volume-slider-height: 8px;
        color: var(--colors-light-800);
      }

      input:hover::-ms-thumb {
        --volume-slider-height: 8px;
        color: var(--colors-light-800);
      }
    `
];
__decorateClass$6([
  property({ type: Number })
], VolumeButton.prototype, "volume", 2);
__decorateClass$6([
  property({ type: Boolean })
], VolumeButton.prototype, "muted", 2);
VolumeButton = __decorateClass$6([
  customElement("vmp-volume-button")
], VolumeButton);
var __defProp$5 = Object.defineProperty;
var __getOwnPropDesc$5 = Object.getOwnPropertyDescriptor;
var __decorateClass$5 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$5(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$5(target, key, result);
  return result;
};
let ProgressScrubber = class extends ThemedLitElement {
  constructor() {
    super(...arguments);
    this.duration = 0;
    this.currentTime = 0;
    this.buffer = [];
    this._containerRef = createRef();
    this._handleContainerResize = () => {
      var _a;
      const containerRect = (_a = this._containerRef.value) == null ? void 0 : _a.getBoundingClientRect();
      this.dispatchEvent(
        new CustomEvent("scrubber:resize", {
          detail: containerRect,
          bubbles: true,
          composed: true
        })
      );
    };
    this._handleMouseEnter = (e) => {
      var _a;
      const containerRect = (_a = this._containerRef.value) == null ? void 0 : _a.getBoundingClientRect();
      this.dispatchEvent(
        new CustomEvent("scrubber:mouseenter", {
          detail: {
            x: e.clientX,
            y: e.clientY,
            elementLeft: (containerRect == null ? void 0 : containerRect.left) ?? 0,
            elementRight: (containerRect == null ? void 0 : containerRect.right) ?? document.body.offsetWidth
          },
          bubbles: true,
          composed: true
        })
      );
    };
    this._handleMouseMove = (e) => {
      var _a;
      const containerRect = (_a = this._containerRef.value) == null ? void 0 : _a.getBoundingClientRect();
      const elementX = e.clientX - ((containerRect == null ? void 0 : containerRect.left) ?? 0);
      const percentageX = elementX / ((containerRect == null ? void 0 : containerRect.width) ?? 1);
      this.dispatchEvent(
        new CustomEvent("scrubber:mousemove", {
          detail: {
            x: e.clientX,
            y: e.clientY,
            time: percentageX * this.duration,
            elementLeft: (containerRect == null ? void 0 : containerRect.left) ?? 0,
            elementRight: (containerRect == null ? void 0 : containerRect.right) ?? document.body.offsetWidth
          },
          bubbles: true,
          composed: true
        })
      );
    };
    this._handleMouseLeave = (e) => {
      this.dispatchEvent(
        new CustomEvent("scrubber:mouseleave", {
          detail: {
            x: e.clientX,
            y: e.clientY
          },
          bubbles: true,
          composed: true
        })
      );
    };
  }
  render() {
    var _a;
    const bufferSegments = ((_a = this.buffer) == null ? void 0 : _a.sort((a, b) => {
      if (a.start < b.start) {
        return -1;
      } else if (b.start < a.start) {
        return 1;
      }
      return 0;
    }).reduce((acc, { start, end }, index) => {
      const prevBuffer = acc[index - 1];
      const roundedStart = Math.floor(start);
      const roundedEnd = Math.ceil(end);
      acc.push({ id: start, start: Math.max(0, roundedStart), end: Math.min(roundedEnd, 100) });
      if (prevBuffer && prevBuffer.end >= start - 1) {
        acc[index].start = prevBuffer.end;
      }
      return acc;
    }, [])) ?? [];
    return html`
      <div
        class="container"
        ${ref(this._containerRef)}
        ${resizeObserver(this._handleContainerResize)}
        @mouseenter=${this._handleMouseEnter}
        @mousemove=${this._handleMouseMove}
        @mouseleave=${this._handleMouseLeave}
      >
        <input
          key="scrubber"
          aria-label="Progress scrubber"
          type="range"
          min="0"
          max=${this.duration}
          step="0.01"
          .value=${this.currentTime.toString()}
          data-testid="scrubber"
        />
        ${repeat(
      bufferSegments,
      ({ id }) => id,
      (segment) => {
        return html`
              <div class="buffer" style=${`left: ${segment.start}%; width: ${segment.end - segment.start}%`}></div>
            `;
      }
    )}
      </div>
    `;
  }
};
ProgressScrubber.styles = [
  ThemedLitElement.styles ?? [],
  css`
      :host {
        --player-scrubber-height: 8px;
        --player-scrubber-shadow-width: 4000px;

        display: flex;
      }

      .container {
        position: relative;
        display: flex;
        width: 100%;
        opacity: 1;
        border-radius: 1000rem;
        overflow: hidden;
        transition: opacity 150ms ease-out;
      }

      input {
        appearance: none !important;
        height: var(--player-scrubber-height);
        width: 100%;
        margin: 0;
        overflow: hidden;
        cursor: pointer;
        background: var(--colors-light-200);
        transition: height 100ms ease-out, transform 100ms ease-out;
      }

      .buffer {
        position: absolute;
        height: var(--player-scrubber-height);
        width: var(--player-buffered-percentage);
        background: var(--colors-light-400);
        pointer-events: none;
        transition: height 100ms ease-out, width 250ms ease-out, left 250ms ease-out;
      }

      .container:hover .buffer {
        height: calc(var(--player-scrubber-height) * 2);
      }

      .container:hover input {
        height: calc(var(--player-scrubber-height) * 2);
      }

      input::-webkit-slider-runnable-track {
        appearance: none !important;
        height: var(--player-scrubber-height);
        color: var(--colors-light-1000);
        z-index: 2;
        transition: height 100ms ease-out;
      }

      input::-moz-range-track {
        appearance: none !important;
        height: var(--player-scrubber-height);
        color: var(--colors-light-1000);
        transition: height 100ms ease-out;
      }

      input::-ms-track {
        appearance: none !important;
        height: var(--player-scrubber-height);
        color: var(--colors-light-1000);
        transition: height 100ms ease-out;
      }

      .container:hover input::-webkit-slider-runnable-track {
        height: calc(var(--player-scrubber-height) * 2);
      }

      .container:hover input::-moz-range-track {
        height: calc(var(--player-scrubber-height) * 2);
      }

      .container:hover input::-ms-track {
        height: calc(var(--player-scrubber-height) * 2);
      }

      input::-webkit-slider-thumb {
        appearance: none !important;
        height: var(--player-scrubber-height) !important;
        width: var(--player-scrubber-height) !important;
        color: var(--colors-light-1000);
        border-radius: 50%;
        border: none;
        background: var(--colors-light-1000);
        box-shadow: calc((var(--player-scrubber-shadow-width) + var(--player-scrubber-height) / 2) * -1) 0 0
          var(--player-scrubber-shadow-width);
        transition: height 100ms ease-out, width 100ms ease-out, box-shadow 100ms ease-out, color 200ms ease-out;
      }

      input::-moz-range-thumb {
        appearance: none !important;
        height: var(--player-scrubber-height) !important;
        width: var(--player-scrubber-height) !important;
        color: var(--colors-light-1000);
        border-radius: 50%;
        border: none;
        background: var(--colors-light-1000);
        box-shadow: calc((var(--player-scrubber-shadow-width) + var(--player-scrubber-height) / 2) * -1) 0 0
          var(--player-scrubber-shadow-width);
        transition: height 100ms ease-out, width 100ms ease-out, box-shadow 100ms ease-out, color 200ms ease-out;
      }

      input::-ms-thumb {
        appearance: none !important;
        height: var(--player-scrubber-height) !important;
        width: var(--player-scrubber-height) !important;
        color: var(--colors-light-1000);
        border-radius: 50%;
        border: none;
        background: var(--colors-light-1000);
        box-shadow: calc((var(--player-scrubber-shadow-width) + var(--player-scrubber-height) / 2) * -1) 0 0
          var(--player-scrubber-shadow-width);
        transition: height 100ms ease-out, width 100ms ease-out, box-shadow 100ms ease-out, color 200ms ease-out;
      }

      .container:hover input::-webkit-slider-thumb {
        --player-scrubber-height: 16px;
        color: var(--colors-light-800);
      }

      .container:hover input::-moz-range-thumb {
        --player-scrubber-height: 16px;
        color: var(--colors-light-800);
      }

      .container:hover input::-ms-thumb {
        --player-scrubber-height: 16px;
        color: var(--colors-light-800);
      }
    `
];
__decorateClass$5([
  property({ type: Number })
], ProgressScrubber.prototype, "duration", 2);
__decorateClass$5([
  property({ type: Number })
], ProgressScrubber.prototype, "currentTime", 2);
__decorateClass$5([
  property({ type: Array })
], ProgressScrubber.prototype, "buffer", 2);
ProgressScrubber = __decorateClass$5([
  customElement("vmp-progress-scrubber")
], ProgressScrubber);
var __defProp$4 = Object.defineProperty;
var __getOwnPropDesc$4 = Object.getOwnPropertyDescriptor;
var __decorateClass$4 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$4(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$4(target, key, result);
  return result;
};
let Controls = class extends ThemedLitElement {
  constructor() {
    super(...arguments);
    this.resizeHider = new ResizeHider(this);
    this.playing = false;
    this.volume = 1;
    this.muted = false;
    this.currentTime = 0;
    this.duration = 0;
    this.level = -1;
    this.playbackRate = 1;
    this.buffer = [];
    this.levels = [];
    this.captions = false;
    this.fullscreen = false;
    this.controls = defaultControls;
    this.volumeMenuOpen = false;
    this.levelsMenuOpen = false;
    this.playbackRateMenuOpen = false;
    this.settingsMenuOpen = false;
  }
  _handleNavigateBackClick() {
    this.dispatchEvent(new CustomEvent("navigate:back", { bubbles: true, composed: true }));
  }
  _handleNavigateForwardClick() {
    this.dispatchEvent(new CustomEvent("navigate:forward", { bubbles: true, composed: true }));
  }
  _handlePlayClick() {
    this.dispatchEvent(new CustomEvent("play:click", { bubbles: true, composed: true, detail: this.playing }));
  }
  _handleCaptionsClick() {
    this.dispatchEvent(new CustomEvent("captions:click", { bubbles: true, composed: true, detail: this.captions }));
  }
  _handleFullscreenClick() {
    this.dispatchEvent(new CustomEvent("fullscreen:click", { bubbles: true, composed: true, detail: this.fullscreen }));
  }
  _handleVolumeMenuToggle({ detail }) {
    this.volumeMenuOpen = detail;
  }
  _handleLevelsMenuOpen({ detail }) {
    this.levelsMenuOpen = detail;
  }
  _handlePlaybackMenuToggle({ detail }) {
    this.playbackRateMenuOpen = detail;
  }
  _handleSettingsMenuToggle({ detail }) {
    this.settingsMenuOpen = detail;
  }
  hideMenus() {
    var _a, _b;
    (_b = Array.from(
      (_a = this.shadowRoot) == null ? void 0 : _a.querySelectorAll("vmp-menu, vmp-tooltip, vmp-volume-button")
    )) == null ? void 0 : _b.forEach((menu) => {
      var _a2;
      (_a2 = menu.tippy) == null ? void 0 : _a2.hide();
    });
  }
  render() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m;
    const levels = ((_a = this.levels) == null ? void 0 : _a.map((level, index) => {
      const resolution = Math.min(level.width, level.height);
      return {
        id: `level-${index.toString()}`,
        event: "level:click",
        label: `${resolution}p`,
        value: {
          resolution,
          level: index
        }
      };
    })) ?? [];
    const levelItems = {
      title: "Quality",
      items: [
        ...levels.reverse(),
        {
          id: "level--1",
          event: "level:click",
          label: "Auto",
          value: {
            level: -1,
            resolution: "auto"
          }
        }
      ]
    };
    const levelItem = {
      id: "levels",
      label: "Quality",
      subItems: levelItems
    };
    const activeLevel = (_b = levelItems.items) == null ? void 0 : _b.find(({ value }) => value.level === this.level);
    const settingsControls = ["levels", "playback-rate", "captions", "fullscreen"].filter(
      (control) => {
        var _a2;
        return (_a2 = this.controls) == null ? void 0 : _a2.includes(control);
      }
    );
    const settingsItems = {
      title: "Settings",
      items: [captionsItem, fullscreenItem, levelItem, playbackItem].filter(
        ({ id }) => settingsControls.includes(id) && this.resizeHider.hiddenControls.includes(id)
      )
    };
    const progressEnabled = (_c = this.controls) == null ? void 0 : _c.includes("progress");
    const navigationEnabled = (_d = this.controls) == null ? void 0 : _d.includes("navigation");
    const playEnabled = (_e = this.controls) == null ? void 0 : _e.includes("play");
    const volumeEnabled = (_f = this.controls) == null ? void 0 : _f.includes("volume");
    const currentTimeEnabled = (_g = this.controls) == null ? void 0 : _g.includes("current-time");
    const durationEnabled = (_h = this.controls) == null ? void 0 : _h.includes("duration");
    const levelsEnabled = (_i = this.controls) == null ? void 0 : _i.includes("levels");
    const playbackRateEnabled = (_j = this.controls) == null ? void 0 : _j.includes("speed");
    const captionsEnabled = (_k = this.controls) == null ? void 0 : _k.includes("captions");
    const fullscreenEnabled = (_l = this.controls) == null ? void 0 : _l.includes("fullscreen");
    const buttonsEnabled = playEnabled || volumeEnabled || currentTimeEnabled || durationEnabled || levelsEnabled || playbackRateEnabled || captionsEnabled || fullscreenEnabled;
    const activeItems = [
      `level-${(_m = this.level) == null ? void 0 : _m.toString()}`,
      `playback-rate-${this.playbackRate.toString()}`,
      this.captions && "captions",
      this.fullscreen && "fullscreen"
    ].filter(Boolean);
    return html`
      <div
        ${this.resizeHider.measure()}
        ${this.resizeHider.contain()}
        class=${classMap({
      container: true,
      hidden: !progressEnabled && !buttonsEnabled
    })}
      >
        <div
          class=${classMap({
      "scrubber-container": true,
      hidden: !progressEnabled
    })}
        >
          <vmp-progress-scrubber
            class=${classMap({
      scrubber: true,
      invisible: this.volumeMenuOpen || this.levelsMenuOpen || this.playbackRateMenuOpen || this.settingsMenuOpen
    })}
            duration=${this.duration}
            currentTime=${this.currentTime}
            .buffer=${this.buffer}
          ></vmp-progress-scrubber>
        </div>
        <div
          ${this.resizeHider.contain()}
          class=${classMap({
      buttons: true,
      hidden: !buttonsEnabled
    })}
        >
          <div class="section">
            <div
              ${this.resizeHider.hide("navigation", 6)}
              class=${classMap({
      navigation: true,
      hidden: !navigationEnabled
    })}
            >
              <vmp-icon-button
                icon="arrow-left"
                weight="heavy"
                rounded="full"
                @click=${this._handleNavigateBackClick}
              ></vmp-icon-button>
              <vmp-icon-button
                icon="arrow-right"
                weight="heavy"
                rounded="full"
                @click=${this._handleNavigateForwardClick}
              ></vmp-icon-button>
            </div>
            <vmp-icon-button
              class=${classMap({
      hidden: !playEnabled
    })}
              icon=${this.playing ? "pause" : "play"}
              weight="heavy"
              rounded="full"
              data-testid="play"
              @click=${this._handlePlayClick}
            ></vmp-icon-button>
            <vmp-volume-button
              class=${classMap({
      hidden: !volumeEnabled
    })}
              ?muted=${this.muted}
              volume=${this.volume}
              @menu:toggle=${this._handleVolumeMenuToggle}
            ></vmp-volume-button>
            <div class="time">
              <vmp-text
                ${this.resizeHider.hide("current-time", 1)}
                class=${classMap({
      hidden: !currentTimeEnabled
    })}
                ?semibold=${true}
                content="${formatDuration(this.currentTime)}${durationEnabled ? "/" : null}"
              ></vmp-text>
              <vmp-text
                ${this.resizeHider.hide("duration", 5)}
                class=${classMap({
      hidden: !durationEnabled
    })}
                ?semibold=${true}
                content=${formatDuration(this.duration)}
              ></vmp-text>
            </div>
          </div>
          <div class="section">
            <vmp-menu
              ${this.resizeHider.hide("levels", 0)}
              class=${classMap({
      hidden: !levelsEnabled || levels.length <= 0
    })}
              .items=${levelItems}
              .activeItems=${activeItems}
              @menu:toggle=${this._handleLevelsMenuOpen}
            >
              <vmp-tooltip content="Quality" ?disabled=${this.levelsMenuOpen}>
                <vmp-button ?active=${this.levelsMenuOpen} icon="chevron-down">${activeLevel == null ? void 0 : activeLevel.label}</vmp-button>
              </vmp-tooltip>
            </vmp-menu>
            <vmp-menu
              ${this.resizeHider.hide("playback-rate", 4)}
              class=${classMap({
      hidden: !playbackRateEnabled
    })}
              .items=${playbackRateItems}
              .activeItems=${activeItems}
              @menu:toggle=${this._handlePlaybackMenuToggle}
            >
              <vmp-tooltip content="Playback speed" ?disabled=${this.playbackRateMenuOpen}>
                <vmp-button ?active=${this.playbackRateMenuOpen} icon="chevron-down"
                  >${this.playbackRate.toFixed(1)}x</vmp-button
                >
              </vmp-tooltip>
            </vmp-menu>
            <vmp-tooltip
              ${this.resizeHider.hide("captions", 3)}
              class=${classMap({
      hidden: !captionsEnabled
    })}
              content="Captions"
            >
              <vmp-icon-button
                ?active=${this.captions}
                icon=${this.captions ? "captions" : "captions-cross"}
                rounded="full"
                @click=${this._handleCaptionsClick}
              ></vmp-icon-button>
            </vmp-tooltip>
            <vmp-tooltip
              ${this.resizeHider.hide("fullscreen", 2)}
              content="Fullscreen"
              class=${classMap({
      hidden: !fullscreenEnabled
    })}
            >
              <vmp-icon-button
                ?active=${this.fullscreen}
                icon=${this.fullscreen ? "minimize" : "maximize"}
                rounded="full"
                data-testid="fullscreen"
                @click=${this._handleFullscreenClick}
              ></vmp-icon-button>
            </vmp-tooltip>
            <vmp-menu
              ${this.resizeHider.show("settings", ["levels", "playback-rate", "captions", "fullscreen"])}
              .items=${settingsItems}
              .activeItems=${activeItems}
              @menu:toggle=${this._handleSettingsMenuToggle}
            >
              <vmp-tooltip content="Settings" ?disabled=${this.settingsMenuOpen}>
                <vmp-icon-button ?active=${this.settingsMenuOpen} icon="settings" rounded="full"></vmp-icon-button>
              </vmp-tooltip>
            </vmp-menu>
          </div>
        </div>
      </div>
    `;
  }
};
Controls.styles = [
  ThemedLitElement.styles ?? [],
  css`
      :host {
        --controls-border-radius: var(--player-border-radius, 0px);

        display: flex;
        width: 100%;
        isolation: isolate;
      }

      .container {
        display: flex;
        flex-direction: column;
        gap: var(--space-small);
        flex: 1;
        min-width: 0;
        overflow: auto;
        padding: var(--space-xl) var(--space-xs) var(--space-xs) var(--space-xs);
        background: linear-gradient(to bottom, transparent 20%, var(--colors-dark-800) 100%);
        border-radius: var(--controls-border-radius);
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }

      .buttons {
        display: flex;
        justify-content: space-between;
        gap: var(--space-xs);
        width: max-content;
        min-width: 100%;
      }

      .section {
        display: flex;
        gap: var(--space-xs);
      }

      .scrubber-container {
        position: relative;
        display: flex;
        height: 8px;
      }

      .scrubber {
        position: absolute;
        width: 100%;
        bottom: 0;
        transition: opacity 150ms ease-out;
      }

      .invisible {
        opacity: 0;
        pointer-events: none;
      }

      .navigation {
        display: flex;
        background: var(--colors-light-1000);
        border-radius: 1000rem;
      }

      .time {
        display: flex;
        align-items: center;
        color: var(--colors-light-1000);
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }

      .hidden {
        display: none;
      }
    `
];
__decorateClass$4([
  property({ type: Boolean })
], Controls.prototype, "playing", 2);
__decorateClass$4([
  property({ type: Number })
], Controls.prototype, "volume", 2);
__decorateClass$4([
  property({ type: Boolean })
], Controls.prototype, "muted", 2);
__decorateClass$4([
  property({ type: Number })
], Controls.prototype, "currentTime", 2);
__decorateClass$4([
  property({ type: Number })
], Controls.prototype, "duration", 2);
__decorateClass$4([
  property({ type: Number })
], Controls.prototype, "level", 2);
__decorateClass$4([
  property({ type: Number })
], Controls.prototype, "playbackRate", 2);
__decorateClass$4([
  property({ type: Array })
], Controls.prototype, "buffer", 2);
__decorateClass$4([
  property({ type: Array, attribute: false })
], Controls.prototype, "levels", 2);
__decorateClass$4([
  property({ type: Boolean })
], Controls.prototype, "captions", 2);
__decorateClass$4([
  property({ type: Boolean })
], Controls.prototype, "fullscreen", 2);
__decorateClass$4([
  property({ type: Array })
], Controls.prototype, "controls", 2);
__decorateClass$4([
  state()
], Controls.prototype, "volumeMenuOpen", 2);
__decorateClass$4([
  state()
], Controls.prototype, "levelsMenuOpen", 2);
__decorateClass$4([
  state()
], Controls.prototype, "playbackRateMenuOpen", 2);
__decorateClass$4([
  state()
], Controls.prototype, "settingsMenuOpen", 2);
Controls = __decorateClass$4([
  customElement("vmp-player-controls")
], Controls);
var __defProp$3 = Object.defineProperty;
var __getOwnPropDesc$3 = Object.getOwnPropertyDescriptor;
var __decorateClass$3 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$3(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$3(target, key, result);
  return result;
};
let LoadingSpinner = class extends ThemedLitElement {
  render() {
    return html`<vmp-icon icon="loading"></vmp-icon>`;
  }
};
LoadingSpinner.styles = [
  ThemedLitElement.styles ?? [],
  css`
      :host {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `
];
LoadingSpinner = __decorateClass$3([
  customElement("vmp-loading-spinner")
], LoadingSpinner);
var __defProp$2 = Object.defineProperty;
var __getOwnPropDesc$2 = Object.getOwnPropertyDescriptor;
var __decorateClass$2 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$2(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$2(target, key, result);
  return result;
};
let Captions = class extends ThemedLitElement {
  constructor() {
    super(...arguments);
    this.caption = "";
  }
  render() {
    return html`
      <div class="container">
        <vmp-text content=${this.caption} size="large"></vmp-text>
      </div>
    `;
  }
};
Captions.styles = [
  ThemedLitElement.styles ?? [],
  css`
      .container {
        display: block;
        margin: 0 var(--space-small);
        text-align: center;
      }

      vmp-text::part(content) {
        display: inline;
        padding: var(--space-xxs) var(--space-xs);
        text-align: center;
        border-radius: var(--radii-small);
        box-decoration-break: clone;
        -webkit-box-decoration-break: clone;
        color: var(--colors-light-1000);
        background: var(--colors-dark-800);
        /* Line height controls the spacing between the cloned boxes so has to override the default */
        line-height: 2.2;
      }
    `
];
__decorateClass$2([
  property({ type: String })
], Captions.prototype, "caption", 2);
Captions = __decorateClass$2([
  customElement("vmp-captions")
], Captions);
var __defProp$1 = Object.defineProperty;
var __getOwnPropDesc$1 = Object.getOwnPropertyDescriptor;
var __decorateClass$1 = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc$1(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp$1(target, key, result);
  return result;
};
const CONTROLS_HIDE_TIMEOUT = 3e3;
let PlayerOverlay = class extends ThemedLitElement {
  constructor() {
    super(...arguments);
    this.loading = false;
    this.playing = false;
    this.volume = 1;
    this.muted = false;
    this.currentTime = 0;
    this.duration = 0;
    this.levels = [];
    this.level = -1;
    this.playbackRate = 1;
    this.caption = "";
    this.buffer = [];
    this.captions = false;
    this.fullscreen = false;
    this.controls = defaultControls;
    this.showControls = true;
    this._hasTouched = false;
    this._playerControlsRef = createRef();
  }
  _startHideControls() {
    clearTimeout(this._controlHideTimeout);
    this._controlHideTimeout = setTimeout(() => {
      this.showControls = false;
      this._hasTouched = false;
    }, CONTROLS_HIDE_TIMEOUT);
  }
  _immediateShowControls() {
    this.showControls = true;
    clearTimeout(this._controlHideTimeout);
  }
  _immediateHideControls() {
    this.showControls = false;
    this._hasTouched = false;
    clearTimeout(this._controlHideTimeout);
  }
  willUpdate(changedProperties) {
    var _a;
    if (changedProperties.has("playing") && this.playing) {
      this._startHideControls();
    }
    if (changedProperties.has("showControls") && this.playing && !this.showControls) {
      (_a = this._playerControlsRef.value) == null ? void 0 : _a.hideMenus();
    }
  }
  render() {
    var _a;
    const playLargeEnabled = (_a = this.controls) == null ? void 0 : _a.includes("play-large");
    const hideControls = this.playing && !this.showControls;
    const showPlaying = !!(playLargeEnabled && !this.playing);
    return html`
      <div
        class=${classMap({
      container: true,
      hide: hideControls
    })}
        @mousemove=${() => {
      this._immediateShowControls();
      this._startHideControls();
    }}
        @mouseleave=${this._immediateHideControls}
        @touchstart=${(e) => {
      if (this.playing && !this.showControls && !this._hasTouched) {
        e.preventDefault();
        this._hasTouched = true;
        this._immediateShowControls();
        this._startHideControls();
      }
    }}
      >
        <div
          class=${classMap({
      fixtures: true,
      invisible: !this.loading && !showPlaying
    })}
          @click=${() => {
      if (this.showControls) {
        this.dispatchEvent(
          new CustomEvent("play:click", { bubbles: true, composed: true, detail: this.playing })
        );
      }
    }}
        >
          <vmp-icon
            class=${classMap({
      hidden: !showPlaying
    })}
            icon="play-large"
          ></vmp-icon>
          <vmp-loading-spinner
            class=${classMap({
      hidden: !this.loading || showPlaying
    })}
          ></vmp-loading-spinner>
        </div>
        <div
          class=${classMap({
      controls: true,
      hide: hideControls
    })}
          @focusin=${this._immediateShowControls}
          @mousemove=${(e) => {
      e.stopPropagation();
      this._immediateShowControls();
    }}
          @click=${this._immediateShowControls}
        >
          ${this.captions ? html`<vmp-captions
                class=${classMap({
      hidden: !this.caption,
      hiddenControls: hideControls
    })}
                caption=${this.caption}
              ></vmp-captions>` : null}
          <vmp-player-controls
            ${ref(this._playerControlsRef)}
            ?playing=${this.playing}
            ?muted=${this.muted}
            volume=${this.volume}
            currentTime=${this.currentTime}
            duration=${this.duration}
            .levels=${this.levels}
            .level=${this.level}
            playbackRate=${this.playbackRate}
            .buffer=${this.buffer}
            .controls=${this.controls}
            ?captions=${this.captions}
            ?fullscreen=${this.fullscreen}
          ></vmp-player-controls>
        </div>
      </div>
    `;
  }
};
PlayerOverlay.styles = [
  ThemedLitElement.styles ?? [],
  css`
      :host {
        --overlay-border-radius: var(--player-border-radius, 0px);
      }

      .container {
        position: relative;
        display: flex;
        justify-content: space-between;
        height: 100%;
        width: 100%;
        overflow: hidden;
        border-radius: var(--overlay-border-radius);
      }

      .container.hide {
        cursor: none;
      }

      .fixtures {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 1;
        color: var(--colors-light-1000);
        background: var(--colors-dark-200);
        border-radius: var(--overlay-border-radius);
        transition: opacity 150ms ease-out;
      }

      .hidden {
        display: none;
      }

      .invisible {
        opacity: 0;
      }

      .controls {
        position: relative;
        display: flex;
        flex-direction: column;
        align-self: flex-end;
        flex: 1;
        min-width: 0;
        opacity: 1;
        isolation: isolate;
        transform: translateY(0%);
        transition: transform 200ms ease-out;
      }

      .controls.hide {
        pointer-events: none;
        transform: translateY(100%);
      }

      vmp-captions {
        position: absolute;
        bottom: calc(100%);
        width: 100%;
        transform: translateY(var(--space-medium));
        transition: transform 200ms ease-out;
      }

      vmp-captions.hiddenControls {
        transform: translateY(calc(var(--space-small) * -1));
      }
    `
];
__decorateClass$1([
  property({ type: Boolean })
], PlayerOverlay.prototype, "loading", 2);
__decorateClass$1([
  property({ type: Boolean })
], PlayerOverlay.prototype, "playing", 2);
__decorateClass$1([
  property({ type: Number })
], PlayerOverlay.prototype, "volume", 2);
__decorateClass$1([
  property({ type: Boolean })
], PlayerOverlay.prototype, "muted", 2);
__decorateClass$1([
  property({ type: Number })
], PlayerOverlay.prototype, "currentTime", 2);
__decorateClass$1([
  property({ type: Number })
], PlayerOverlay.prototype, "duration", 2);
__decorateClass$1([
  property({ type: Array, attribute: false })
], PlayerOverlay.prototype, "levels", 2);
__decorateClass$1([
  property({ type: Number })
], PlayerOverlay.prototype, "level", 2);
__decorateClass$1([
  property({ type: Number })
], PlayerOverlay.prototype, "playbackRate", 2);
__decorateClass$1([
  property({ type: String })
], PlayerOverlay.prototype, "caption", 2);
__decorateClass$1([
  property({ type: Array })
], PlayerOverlay.prototype, "buffer", 2);
__decorateClass$1([
  property({ type: Boolean })
], PlayerOverlay.prototype, "captions", 2);
__decorateClass$1([
  property({ type: Boolean })
], PlayerOverlay.prototype, "fullscreen", 2);
__decorateClass$1([
  property({ type: Array })
], PlayerOverlay.prototype, "controls", 2);
__decorateClass$1([
  state()
], PlayerOverlay.prototype, "showControls", 2);
PlayerOverlay = __decorateClass$1([
  customElement("vmp-player-overlay")
], PlayerOverlay);
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
const FPS_60 = 1e3 / 60;
let MediaPlayer = class extends ThemedLitElement {
  constructor() {
    super(...arguments);
    this.burnCaptions = false;
    this.debug = false;
    this.propAspectRatio = 0;
    this.preload = "auto";
    this.autoplay = false;
    this.loading = false;
    this.compilation = crossFade;
    this._sceneController = new SceneController$1(this);
    this._drawController = new DrawController(this);
    this._observer = null;
    this._wasPlaying = false;
    this._manualMute = this.muted;
    this._seekerRAF = null;
    this._seekerTimeout = null;
    this._lastWaiting = false;
    this._lastTimestamp = null;
    this._playerRef = createRef();
    this._canvasRef = createRef();
    this._pageHidden = document.visibilityState === "hidden";
    this._aspectRatio = this.propAspectRatio;
    this._processing = false;
    this._waiting = false;
    this._errors = [];
    this._seeking = false;
    this._captions = false;
    this._fullscreen = false;
    this._initialised = null;
    this._paused = true;
    this._playbackRate = 1;
    this._volume = null;
    this._muted = null;
    this._videoResolution = -1;
    this._handleContainerResize = () => {
      var _a;
      if (((_a = this.shadowRoot) == null ? void 0 : _a.host) && !this.propAspectRatio) {
        this._aspectRatio = this.shadowRoot.host.clientWidth / this.shadowRoot.host.clientHeight;
      }
    };
    this._handleCanvasResize = () => {
      this.draw();
    };
    this._loop = () => {
      this._endLoop();
      if (!this._pageHidden) {
        this._seekerRAF = requestAnimationFrame(this._seeker);
      } else {
        this._seekerTimeout = setTimeout(this._seeker, FPS_60);
      }
    };
    this._endLoop = () => {
      if (this._seekerRAF) {
        cancelAnimationFrame(this._seekerRAF);
      }
      if (this._seekerTimeout) {
        clearTimeout(this._seekerTimeout);
      }
      this._seekerRAF = null;
      this._seekerTimeout = null;
    };
    this._seeker = (timestamp = performance.now()) => {
      if (this.sceneConfig && this.scenes.length > 0) {
        this._waiting = this._drawController.activeScenes.some(({ video }) => {
          if (video) {
            const { key } = video;
            const activeVideoState = this.videoState[key];
            return !activeVideoState || !activeVideoState.initialised || activeVideoState.waiting || // Video only has a few frames available
            video.node.readyState < 3;
          }
        });
        if (this.waiting) {
          this._lastTimestamp = null;
          if (!this._lastWaiting) {
            this.dispatchEvent(new CustomEvent("waiting"));
          }
        } else {
          if (this._lastWaiting) {
            this.dispatchEvent(new CustomEvent("playing"));
          }
        }
        if (timestamp - (this._lastTimestamp ?? 0) > 1e3 / this.sceneConfig.frameRate) {
          this._drawController.currentTime = this._drawController.currentTime + (timestamp - (this._lastTimestamp ?? timestamp)) / 1e3 * this.playbackRate;
          this._lastTimestamp = timestamp;
        }
        this._lastWaiting = this._waiting;
        if (this._drawController.currentTime >= this.duration) {
          this.pause();
          this.dispatchEvent(new CustomEvent("ended"));
        }
      }
      if (!this.paused) {
        this._loop();
      }
    };
    this._handleError = (e) => {
      this._errors = [...this.errors, e.error];
      if (e.error.fatal) {
        this.pause();
      }
    };
    this._handleFullscreenChange = () => {
      if (document.fullscreenElement) {
        this._fullscreen = true;
      } else {
        this._fullscreen = false;
      }
    };
    this._handleVisibilityChange = () => {
      this._pageHidden = document.visibilityState === "hidden";
      if (!this.paused) {
        this._loop();
      }
    };
  }
  get aspectRatio() {
    return this._aspectRatio;
  }
  get processing() {
    return this._processing;
  }
  get waiting() {
    return !this.paused && this._waiting;
  }
  get errors() {
    return this._errors;
  }
  get seeking() {
    return this._seeking;
  }
  get captions() {
    return this._captions;
  }
  get fullscreen() {
    return this._fullscreen;
  }
  get initialised() {
    this._initialised = this._initialised ?? new Promise((resolve) => {
      const timer = setInterval(() => {
        const hasFatalErrored = !!this.errors.find((error) => error.fatal);
        if (hasFatalErrored) {
          resolve(false);
        }
        if (!this.loading && !this.processing && this.scenes.length > 0) {
          resolve(true);
        }
      }, 500);
      setTimeout(() => {
        clearInterval(timer);
        resolve(false);
      }, 60 * 1e3);
    });
    return this._initialised;
  }
  get duration() {
    return this._sceneController.duration;
  }
  get scene() {
    return this._drawController.activeScenes[this._drawController.activeScenes.length - 1];
  }
  get scenes() {
    var _a;
    return ((_a = this._sceneController.scenes) == null ? void 0 : _a.scenes) ?? [];
  }
  get sceneConfig() {
    return this._sceneController.scenes;
  }
  get videos() {
    return this._sceneController.videos ?? {};
  }
  get videoState() {
    return this._sceneController.videoState;
  }
  get sceneVideoState() {
    return this.scenes.reduce((acc, { video, start, duration }) => {
      const key = (video == null ? void 0 : video.key) ?? `${start}-${duration}`;
      acc[key] = this.videoState[key];
      return acc;
    }, {});
  }
  set paused(value) {
    if (value !== this._paused) {
      this._paused = value;
      this.requestUpdate();
      this.dispatchEvent(new CustomEvent(value ? "pause" : "play"));
    }
  }
  get paused() {
    return this._paused;
  }
  set playbackRate(value) {
    if (value !== this._playbackRate) {
      this._playbackRate = value;
      this.scenes.forEach(({ video }) => {
        if (video) {
          video.node.playbackRate = value;
        }
      });
      this.requestUpdate();
      this.dispatchEvent(new CustomEvent("ratechange", { detail: value }));
    }
  }
  get playbackRate() {
    return this._playbackRate;
  }
  set volume(value) {
    const clampedValue = Math.min(1, Math.max(0, value));
    if (clampedValue !== this._volume) {
      this.scenes.forEach(({ video }) => {
        if (video) {
          video.node.volume = clampedValue;
        }
      });
      localStorage.setItem("vmp-player-volume", clampedValue.toString());
      this._volume = clampedValue;
      if (clampedValue > 0) {
        this.muted = false;
      }
      this.requestUpdate();
      this.dispatchEvent(new CustomEvent("volumechange", { detail: value }));
    }
  }
  get volume() {
    return this._volume ?? (this._volume = parseFloat(localStorage.getItem("vmp-player-volume") ?? "1"));
  }
  set muted(value) {
    this.scenes.forEach(({ video }) => {
      if (video) {
        video.node.muted = value;
      }
    });
    if (value !== this._muted) {
      this._muted = value;
      localStorage.setItem("vmp-player-muted", value.toString());
      this.requestUpdate();
      this.dispatchEvent(new CustomEvent("volumechange", { detail: value ? 0 : this.volume }));
    }
  }
  get muted() {
    return this._muted ?? (this._muted = this.autoplay || localStorage.getItem("vmp-player-muted") === "true");
  }
  set videoResolution(value) {
    if (value !== this._videoResolution) {
      this._videoResolution = value;
      this.scenes.forEach(({ video }) => {
        if (video == null ? void 0 : video.hls) {
          const levelIndex = video.hls.levels.findIndex((level) => Math.min(level.height, level.width) === value);
          video.hls.currentLevel = levelIndex ?? -1;
        }
      });
      this.requestUpdate();
    }
  }
  get videoResolution() {
    return this._videoResolution;
  }
  set currentTime(value) {
    if (value !== this._drawController.currentTime) {
      this._drawController.currentTime = value;
      this._sceneController.setVideosCurrentTime(value);
    }
  }
  get currentTime() {
    return this._drawController.currentTime;
  }
  reset(time = 0, play = false) {
    this._lastTimestamp = null;
    this._drawController.currentTime = time;
    this._sceneController.setVideosCurrentTime(time);
    if (play) {
      this.play();
    } else {
      this.pause();
    }
  }
  clear() {
    this._drawController.clear();
  }
  play() {
    this.paused = false;
    this._lastTimestamp = null;
    if (this.duration > 0 && this._drawController.currentTime >= this.duration) {
      this.reset(0, true);
    }
    this._loop();
  }
  pause() {
    this._endLoop();
    this.paused = true;
    this.scenes.forEach(({ video }) => {
      if (video) {
        video.node.pause();
      }
    });
  }
  draw(time) {
    return this._drawController.draw(time);
  }
  setScene(index) {
    var _a, _b;
    if ((_a = this._sceneController) == null ? void 0 : _a.scenes) {
      const scene = (_b = this.scenes) == null ? void 0 : _b[index];
      if (!scene) {
        const error = new MediaPlayerError(`No scene at index: ${index}`);
        return this.dispatchEvent(
          new ErrorEvent("error", { composed: true, bubbles: true, error, message: error.message })
        );
      }
      this.reset(scene.start + this._sceneController.scenes.transitionDuration, !this.paused);
    }
  }
  // This must be called in an event handler that occurs as a result of user action as the browser does not allow programmatic fullscreen
  toggleFullscreen(active) {
    if (active ?? this.fullscreen) {
      exitFullscreen();
    } else {
      if (this._playerRef.value) {
        enterFullScreen(this._playerRef.value);
      }
    }
  }
  connectedCallback() {
    var _a;
    super.connectedCallback();
    this.addEventListener("error", this._handleError);
    document.addEventListener("fullscreenchange", this._handleFullscreenChange);
    document.addEventListener("visibilitychange", this._handleVisibilityChange);
    if ((_a = this.shadowRoot) == null ? void 0 : _a.host) {
      this._observer = new ResizeObserver(this._handleContainerResize);
      this._observer.observe(this.shadowRoot.host);
    }
  }
  willUpdate() {
    if (this.propAspectRatio) {
      this._aspectRatio = this.propAspectRatio;
    }
  }
  updated(changed) {
    if (changed.has("propAspectRatio") && !this.propAspectRatio) {
      this._handleContainerResize();
    }
    if (
      // We want to autoplay after the video has processed for the first time
      // but only want it to activate once per session
      this.autoplay && !this.processing && !this._sceneController.hasPlayed && changed.has("_processing")
    ) {
      this.play();
    }
  }
  disconnectedCallback() {
    var _a;
    super.disconnectedCallback();
    (_a = this._observer) == null ? void 0 : _a.disconnect();
    document.removeEventListener("fullscreenchange", this._handleFullscreenChange);
    document.removeEventListener("visibilitychange", this._handleVisibilityChange);
  }
  _renderStyles() {
    if (!this.propAspectRatio) {
      return html`
        <style>
          :host {
            width: 100%;
            height: 100%;
          }
        </style>
      `;
    }
    if (typeof this.propAspectRatio === "number") {
      return html`
        <style>
          :host {
            aspect-ratio: ${this.propAspectRatio};
          }
        </style>
      `;
    }
    return null;
  }
  _renderError() {
    return html`<div class="error">
      <div class="error-icon">
        <vmp-icon icon="error"></vmp-icon>
      </div>
      <div class="error-text">
        <vmp-text semibold content="An error has occurred when attempting to play this video."></vmp-text>
        <vmp-text semibold content="Please try again later."></vmp-text>
      </div>
    </div>`;
  }
  render() {
    var _a, _b, _c, _d, _e, _f;
    const disabled = this.loading || this.processing || !this.scenes || this.duration <= 0;
    const { height, width } = getSizeDetails({
      resolution: ((_a = this.sceneConfig) == null ? void 0 : _a.resolution) ?? 720,
      aspectRatio: this.aspectRatio
    });
    const buffer = Object.keys(this.sceneVideoState).reduce((acc, key) => {
      return acc.concat(this.sceneVideoState[key].buffered ?? []);
    }, []);
    const controls = (
      // Don't show any controls when disabled because we haven't created the `videos` yet
      // This means that if the user was to play the video then it would have to start muted because we can only unmute videos
      // within user events for autoplay reasons
      disabled ? [] : (_b = this.controls ?? defaultControls) == null ? void 0 : _b.filter((control) => {
        if (control === "fullscreen" && isiOS()) {
          return false;
        }
        if (control === "navigation") {
          if (!this.scenes || this.scenes.length <= 1) {
            return false;
          }
        }
        return true;
      })
    );
    const previewDisabled = !(controls == null ? void 0 : controls.includes("preview"));
    const lastSceneIndex = (((_c = this.scenes) == null ? void 0 : _c.length) ?? 0) - 1;
    const activeSceneIndex = ((_d = this.scenes) == null ? void 0 : _d.findIndex((scene) => scene === this._drawController.activeScenes[0])) ?? 0;
    const activeHls = (_f = (_e = this._drawController.activeScenes[0]) == null ? void 0 : _e.video) == null ? void 0 : _f.hls;
    const showSpinner = this.loading || this.waiting || this.processing;
    const noValidSource = !this.src && !this.data;
    const hasFatalErrored = !!this.errors.find((error) => error.fatal);
    return html`
      ${this._renderStyles()}
      <div
        ${ref(this._playerRef)}
        class=${classMap({
      player: true,
      responsive: !this.propAspectRatio
    })}
        style=${styleMap({
      aspectRatio: this.aspectRatio
    })}
      >
        ${!showSpinner && (noValidSource || hasFatalErrored) ? this._renderError() : html`<canvas
                ${ref(this._canvasRef)}
                ${this._drawController.canvas()}
                ${resizeObserver(this._handleCanvasResize)}
                height=${height}
                width=${width}
              ></canvas>
              <vmp-player-overlay
                ${this._drawController.controls()}
                .data=${this.data}
                .template=${this.template}
                ?disabled=${disabled}
                ?playing=${!this.paused}
                ?loading=${showSpinner}
                ?muted=${this.muted}
                volume=${this.volume}
                duration=${this.duration}
                .levels=${(activeHls == null ? void 0 : activeHls.levels) ?? []}
                level=${ifDefined((activeHls == null ? void 0 : activeHls.autoLevelEnabled) ? -1 : activeHls == null ? void 0 : activeHls.currentLevel)}
                playbackRate=${this.playbackRate}
                caption=${this._drawController.caption}
                .buffer=${buffer}
                .controls=${controls}
                ?captions=${this.captions}
                ?fullscreen=${this.fullscreen}
                @navigate:back=${(event) => {
      if (!event.defaultPrevented) {
        this.setScene(activeSceneIndex <= 0 ? lastSceneIndex : activeSceneIndex - 1);
      }
    }}
                @navigate:forward=${(event) => {
      if (!event.defaultPrevented) {
        this.setScene(activeSceneIndex >= lastSceneIndex ? 0 : activeSceneIndex + 1);
      }
    }}
                @play:click=${({ detail: playing }) => {
      if (!disabled) {
        if (playing) {
          this.pause();
        } else {
          this.muted = this._manualMute;
          this.play();
        }
      }
    }}
                @volume:click=${({ detail: muted }) => {
      this.muted = !muted;
      this._manualMute = !muted;
    }}
                @level:click=${({ detail }) => {
      this.videoResolution = detail.value.resolution;
    }}
                @playbackRate:click=${({ detail }) => {
      this.playbackRate = detail.value ?? 1;
    }}
                @captions:click=${() => {
      this._captions = !this._captions;
    }}
                @fullscreen:click=${() => {
      this.toggleFullscreen();
    }}
                @input=${(event) => {
      const target = event.composedPath()[0];
      const key = target.getAttribute("key");
      if (key === "scrubber") {
        const time = parseFloat(target.value);
        this._drawController.currentTime = time;
        this._sceneController.setVideosCurrentTime(time);
        this.dispatchEvent(new CustomEvent("seeking", { detail: time }));
      }
      if (key === "volume") {
        this.volume = parseFloat(target.value);
      }
    }}
                @pointerdown=${(event) => {
      const target = event.composedPath()[0];
      const key = target.getAttribute("key");
      if (key === "scrubber") {
        this._seeking = true;
        this._wasPlaying = !this.paused;
        if (!this.paused) {
          this.pause();
        }
      }
    }}
                @pointerup=${(event) => {
      const target = event.composedPath()[0];
      const key = target.getAttribute("key");
      if (key === "scrubber") {
        this._seeking = false;
        if (this._wasPlaying) {
          this.play();
          this._wasPlaying = false;
        }
        this.dispatchEvent(new CustomEvent("seeked", { detail: this._drawController.currentTime }));
      }
    }}
              ></vmp-player-overlay>
              <vmp-video-preview
                ?disabled=${previewDisabled}
                .src=${this.src}
                .data=${this.data}
                .template=${this.template}
                .aspectRatio=${this.aspectRatio}
                .compilation=${this.compilation}
              ></vmp-video-preview>`}
      </div>
    `;
  }
};
MediaPlayer.styles = [
  ThemedLitElement.styles ?? [],
  css`
      :host {
        --player-border-radius: var(--vouch-media-player-border-radius, 0px);

        display: flex;
      }

      .player {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--colors-dark-1000);
        border-radius: var(--player-border-radius);
      }

      .responsive {
        width: 100%;
        height: 100%;
      }

      .error {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-xxs);
        padding: var(--space-medium);
        width: 100%;
        height: 100%;
        color: white;
      }

      .error-icon {
        color: var(--colors-red-500);
      }

      .error-text {
        display: flex;
        flex-direction: column;
      }

      canvas {
        width: 100%;
        max-height: 100%;
        object-fit: contain;
        border-radius: var(--player-border-radius);
      }

      vmp-player-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }

      vmp-video-preview {
        position: absolute;
        top: 0;
        left: 0;
      }
    `
];
__decorateClass([
  property({ type: Object })
], MediaPlayer.prototype, "data", 2);
__decorateClass([
  property({ type: Object })
], MediaPlayer.prototype, "template", 2);
__decorateClass([
  property({ type: Boolean })
], MediaPlayer.prototype, "burnCaptions", 2);
__decorateClass([
  property({ type: Boolean })
], MediaPlayer.prototype, "debug", 2);
__decorateClass([
  property({ type: String })
], MediaPlayer.prototype, "src", 2);
__decorateClass([
  property({ type: String })
], MediaPlayer.prototype, "track", 2);
__decorateClass([
  property({ type: Number, attribute: "aspectratio" })
], MediaPlayer.prototype, "propAspectRatio", 2);
__decorateClass([
  property({ type: String })
], MediaPlayer.prototype, "preload", 2);
__decorateClass([
  property({ type: Boolean })
], MediaPlayer.prototype, "autoplay", 2);
__decorateClass([
  property({ type: Boolean })
], MediaPlayer.prototype, "loading", 2);
__decorateClass([
  property({ type: Array })
], MediaPlayer.prototype, "controls", 2);
__decorateClass([
  property({ attribute: false })
], MediaPlayer.prototype, "compilation", 2);
__decorateClass([
  state()
], MediaPlayer.prototype, "_pageHidden", 2);
__decorateClass([
  state()
], MediaPlayer.prototype, "_aspectRatio", 2);
__decorateClass([
  state()
], MediaPlayer.prototype, "_processing", 2);
__decorateClass([
  state()
], MediaPlayer.prototype, "_waiting", 2);
__decorateClass([
  state()
], MediaPlayer.prototype, "_errors", 2);
__decorateClass([
  state()
], MediaPlayer.prototype, "_seeking", 2);
__decorateClass([
  state()
], MediaPlayer.prototype, "_captions", 2);
__decorateClass([
  state()
], MediaPlayer.prototype, "_fullscreen", 2);
MediaPlayer = __decorateClass([
  customElement("vmp-media-player")
], MediaPlayer);
export {
  MediaPlayer
};
//# sourceMappingURL=media-player.js.map
